// @ts-nocheck
/**
 * Nexode Air Template（自定义元素 `nexode-air-template-section`）
 *
 * 依赖：GSAP、ScrollTrigger。
 * 功能：统一注册专题滚动动效、轮播交互、站内播放、导航吸顶与 CTA 交互。
 * 断点：脚本断点与 CSS 保持一致（`max-width: 1023.98px`）。
 * 对外：`window.UgNexodeAirTemplateReveal`（init / refresh / defaults），用于动态内容二次挂载渐显。
 */
(function () {
  'use strict';

  const TAG_NAME = 'nexode-air-template-section';
  const FORM_SUBMIT_URL = 'https://ecf.cirkleinc.com/api/form-submit';
  const MOBILE_MQ = '(max-width: 1023.98px)';
  const AIR65_PIN_LAST_FRAME_INDEX = 117;
  const AIR65_FRAME_EPS = 0.002;
  const AIR65_SCRUB = true;
  const AIR65_PRELOAD_HIGH_COUNT_DESKTOP = 16;
  const AIR65_PRELOAD_HIGH_COUNT_MOBILE = 6;
  const NA_SCROLL_REVEAL_START = 'top 70%';
  /** KV 导航锚点：`scrollTop = 目标文档位置 - 导航高 - 呼吸隙 + 额外向下滚动`（正数=再往下滚一点，内容相对上移） */
  const NA_KV_NAV_ANCHOR_GAP_BELOW_NAV_PX = 8;
  const NA_KV_NAV_ANCHOR_EXTRA_SCROLL_PX = 20;
  let nexodeAirRefreshRaf = 0;

  /**
   * 下一帧调用 ScrollTrigger.refresh，合并多次布局变更，避免重复刷新。
   */
  function scheduleNexodeAirTemplateScrollTriggerRefresh() {
    const ScrollTrigger = window.ScrollTrigger;
    if (!ScrollTrigger || typeof ScrollTrigger.refresh !== 'function') return;
    if (nexodeAirRefreshRaf) return;
    nexodeAirRefreshRaf = window.requestAnimationFrame(() => {
      nexodeAirRefreshRaf = 0;
      try {
        ScrollTrigger.refresh();
      } catch (_) {}
    });
  }

  /** 系统是否开启「减少动态效果」 */
  function naPrefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /** 注销 `registered` 中记录的 ScrollTrigger 实例 */
  function naKillRegisteredTriggers(registered) {
    registered.forEach((st) => {
      try {
        if (st && typeof st.kill === 'function') {
          st.kill();
        }
      } catch (_) {}
    });
  }

  const REVEAL_DEFAULTS = Object.freeze({
    y: 26,
    duration: 0.88,
    ease: 'power2.out',
    start: NA_SCROLL_REVEAL_START,
    stagger: 0.18
  });

  /** 静音 + playsInline；iOS 须 H.264（WebM 常不可播）— 与 UGREEN air 一致 */
  function naPrimeInlineVideoForAutoplay(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    try {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('x5-playsinline', '');
    } catch (_) {}
  }

  /**
   * 内联视频：双 rAF 后 play；`callLoad` 为 true 时在未就绪分支末尾调用 `load()`（TVC / 观察器重试），
   * false 则不调 load（KV 断点切换，避免清空已缓冲）。
   * @param {HTMLVideoElement} video
   * @param {boolean} callLoad
   */
  function naPlayInlineVideo(video, callLoad) {
    if (!(video instanceof HTMLVideoElement)) {
      return;
    }
    naPrimeInlineVideoForAutoplay(video);
    const run = () => {
      try {
        const p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {});
        }
      } catch (_) {}
    };
    const schedulePlay = () => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(run));
    };
    if (video.readyState >= 2) {
      schedulePlay();
      return;
    }
    const once = () => {
      schedulePlay();
    };
    video.addEventListener('loadeddata', once, { once: true });
    video.addEventListener('canplay', once, { once: true });
    if (callLoad) {
      try {
        video.load();
      } catch (_) {}
    }
  }

  function naTryPlayInlineVideo(video) {
    naPlayInlineVideo(video, true);
  }

  function naPlayBufferedInlineVideo(video) {
    naPlayInlineVideo(video, false);
  }

  /** TVC 内联：滚入视口 `naTryPlayInlineVideo`（含 load）— 与 UGREEN `naObserveAndRetryVideoPlay` 一致 */
  function naObserveAndRetryVideoPlay(video) {
    if (!(video instanceof HTMLVideoElement)) return () => {};
    naTryPlayInlineVideo(video);
    if (typeof IntersectionObserver === 'undefined') return () => {};
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) naTryPlayInlineVideo(video);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -4% 0px' }
    );
    io.observe(video);
    return () => {
      try {
        io.disconnect();
      } catch (_) {}
    };
  }

  /** 与 UGREEN air：仅 `[data-na-tvc]` 内 `.ug-nexode-air-template__tvc-video` */
  function initNexodeAirTvcInlineVideo(root, ctx) {
    const { cleanups } = ctx;
    root.querySelectorAll('[data-na-tvc] video.ug-nexode-air-template__tvc-video').forEach((el) => {
      if (!(el instanceof HTMLVideoElement)) return;
      const stop = naObserveAndRetryVideoPlay(el);
      cleanups.push(stop);
    });
  }

  if (customElements.get(TAG_NAME)) {
    return;
  }

  /**
   * 通用滚动渐显：处理 `data-na-reveal` 与 `data-na-reveal-stagger`。
   * @param {HTMLElement} root
   * @returns {() => void}
   */
  function initNexodeAirTemplateScrollReveal(root) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const registered = [];
    const markRevealTrigger = (st) => {
      if (!st || typeof st !== 'object') return;
      try {
        st.__naReveal = true;
      } catch (_) {}
    };

    if (!gsap || !ScrollTrigger) {
      return function noop() {};
    }

    gsap.registerPlugin(ScrollTrigger);

    const reduced = naPrefersReducedMotion();

    if (reduced) {
      root.querySelectorAll('[data-na-reveal], [data-na-reveal-stagger] [data-na-reveal]').forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.opacity = '1';
          el.style.transform = 'none';
        }
      });
      return function noop() {};
    }

    const fromVars = { opacity: 0, y: REVEAL_DEFAULTS.y };
    const { duration, ease, start, stagger } = REVEAL_DEFAULTS;

    /**
     * 收集 stagger 根下参与错开的 `data-na-reveal`（允许中间隔着无 reveal 的包装层，如 `.hero-text`）；
     * 若某 reveal 的祖先链上（止于 stagger 根）另有 `data-na-reveal`，则排除内层，避免嵌套双动画。
     */
    const collectStaggerRevealElements = (staggerRoot) => {
      if (!(staggerRoot instanceof HTMLElement)) return [];
      /** @type {HTMLElement[]} */
      const out = [];
      staggerRoot.querySelectorAll(':scope [data-na-reveal]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        let p = el.parentElement;
        while (p && p !== staggerRoot) {
          if (p.hasAttribute('data-na-reveal')) return;
          p = p.parentElement;
        }
        if (p === staggerRoot) out.push(el);
      });
      return out;
    };

    /** 仅小屏做滚动渐显（与 section 内 `data-na-reveal` + 下列类名一致） */
    const REVEAL_MOBILE_ONLY_SELECTOR =
      '.ug-nexode-air-template__colors-inner[data-na-colors-inner-reveal],' +
      '.ug-nexode-air-template__why-air-grid,' +
      '.ug-nexode-air-template__travel-kit-mo-root';

    const isRevealMobileOnly = (el) =>
      el instanceof HTMLElement && el.matches(REVEAL_MOBILE_ONLY_SELECTOR);

    root.querySelectorAll('[data-na-reveal-stagger]').forEach((staggerRoot) => {
      if (!(staggerRoot instanceof HTMLElement)) return;
      if (staggerRoot.closest('.ug-nexode-air-template__signup-card')) return;
      const children = collectStaggerRevealElements(staggerRoot);
      if (!children.length) return;

      gsap.set(children, fromVars);
      const tween = gsap.to(children, {
        opacity: 1,
        y: 0,
        duration,
        stagger,
        ease,
        scrollTrigger: {
          trigger: staggerRoot,
          start,
          toggleActions: 'play none none none'
        }
      });
      if (tween.scrollTrigger) {
        markRevealTrigger(tween.scrollTrigger);
        registered.push(tween.scrollTrigger);
      }
    });

    root.querySelectorAll('[data-na-reveal]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.closest('[data-na-reveal-stagger]')) return;
      if (el.closest('.ug-nexode-air-template__signup-card')) return;
      if (isRevealMobileOnly(el)) return;

      gsap.set(el, fromVars);
      const tween = gsap.to(el, {
        opacity: 1,
        y: 0,
        duration,
        ease,
        scrollTrigger: {
          trigger: el,
          start,
          toggleActions: 'play none none none'
        }
      });
      if (tween.scrollTrigger) {
        markRevealTrigger(tween.scrollTrigger);
        registered.push(tween.scrollTrigger);
      }
    });

    root.querySelectorAll('.ug-nexode-air-template__signup-card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      gsap.set(card, { autoAlpha: 0, y: 36 });
      const tween = gsap.to(card, {
        autoAlpha: 1,
        y: 0,
        duration: 0.95,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: card,
          start: NA_SCROLL_REVEAL_START,
          once: true
        }
      });

      if (tween.scrollTrigger) {
        markRevealTrigger(tween.scrollTrigger);
        registered.push(tween.scrollTrigger);
      }
    });

    const moRevealEls = Array.from(root.querySelectorAll('[data-na-reveal]')).filter((node) => {
      if (!(node instanceof HTMLElement)) return false;
      if (node.closest('[data-na-reveal-stagger]')) return false;
      if (node.closest('.ug-nexode-air-template__signup-card')) return false;
      return isRevealMobileOnly(node);
    });
    const revealMoMatchMedia = [];
    if (moRevealEls.length) {
      const mm = ScrollTrigger.matchMedia({
        [MOBILE_MQ]: () => {
          const tweens = [];
          moRevealEls.forEach((el) => {
            gsap.set(el, fromVars);
            const tween = gsap.to(el, {
              opacity: 1,
              y: 0,
              duration,
              ease,
              scrollTrigger: {
                trigger: el,
                start,
                toggleActions: 'play none none none'
              }
            });
            if (tween.scrollTrigger) {
              markRevealTrigger(tween.scrollTrigger);
            }
            tweens.push(tween);
          });
          return () => {
            tweens.forEach((tw) => {
              try {
                tw.kill();
              } catch (_) {}
            });
            moRevealEls.forEach((el) => {
              try {
                gsap.set(el, { opacity: 1, y: 0, clearProps: 'opacity,transform' });
              } catch (_) {}
            });
          };
        }
      });
      revealMoMatchMedia.push(mm);
    }

    root.querySelectorAll('.ug-nexode-air-template__signup-visual').forEach((visual) => {
      if (!(visual instanceof HTMLElement)) return;

      const trigger = visual.closest('.ug-nexode-air-template__signup-card') || visual;
      gsap.set(visual, { autoAlpha: 0, y: 36 });
      const tween = gsap.to(visual, {
        autoAlpha: 1,
        y: 0,
        duration: 0.95,
        ease: 'power2.out',
        scrollTrigger: {
          trigger,
          start: NA_SCROLL_REVEAL_START,
          once: true
        }
      });

      if (tween.scrollTrigger) {
        markRevealTrigger(tween.scrollTrigger);
        registered.push(tween.scrollTrigger);
      }
    });

    if (!reduced) {
      const emailIconFrom = { autoAlpha: 0, y: 26, scale: 0.992 };
      const emailIconEase = 'power3.out';
      const emailIconDuration = 0.88;
      const emailIconStart = NA_SCROLL_REVEAL_START;

      root.querySelectorAll('[data-na-email-input-icon]').forEach((icon) => {
        if (!(icon instanceof HTMLElement)) return;
        const trigger =
          icon.closest('.ug-nexode-air-template__email-row') ||
          icon.closest('.ug-nexode-air-template__email-form') ||
          root;
        gsap.set(icon, emailIconFrom);
        const tween = gsap.to(icon, {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: emailIconDuration,
          ease: emailIconEase,
          scrollTrigger: {
            trigger,
            start: emailIconStart,
            once: true
          }
        });
        if (tween.scrollTrigger) {
          markRevealTrigger(tween.scrollTrigger);
          registered.push(tween.scrollTrigger);
        }
      });
    } else {
      root.querySelectorAll('[data-na-email-input-icon]').forEach((icon) => {
        if (icon instanceof HTMLElement) {
          icon.style.opacity = '1';
          icon.style.transform = 'none';
        }
      });
    }

    scheduleNexodeAirTemplateScrollTriggerRefresh();

    return function cleanup() {
      revealMoMatchMedia.forEach((mm) => {
        try {
          mm.revert();
        } catch (_) {}
      });
      revealMoMatchMedia.length = 0;
      naKillRegisteredTriggers(registered);
      registered.length = 0;
    };
  }

  /**
   * 动态插入 DOM 后重跑渐显：先 kill root 内带 `__naReveal` 标记的实例，再 `initNexodeAirTemplateScrollReveal`。
   * @param {HTMLElement} root
   */
  function refreshNexodeAirTemplateScrollReveal(root) {
    const ST = window.ScrollTrigger;
    if (!(root instanceof HTMLElement) || typeof ST === 'undefined') {
      return function noop() {};
    }
    ST.getAll().forEach((st) => {
      if (st.__naReveal && st.trigger && root.contains(/** @type {Node} */ (st.trigger))) {
        st.kill();
      }
    });
    return initNexodeAirTemplateScrollReveal(root);
  }

  /**
   * 双层标题扫光：写入行内 `--na-text-reveal`（0–100%，与 nexode-air-template-deferred.css 蒙版一致）。
   * @param {HTMLElement | null} el
   * @param {number} progress 0–100
   */
  function setNexodeAirTemplateTextReveal(el, progress) {
    if (!(el instanceof HTMLElement)) return;
    const safe = Math.max(0, Math.min(100, progress));
    el.style.setProperty('--na-text-reveal', safe + '%');
  }

  /**
   * Colors Tab：据 `data-active-bg` 等解析出的背景色计算标签前景（亮度阈值 0.62）。
   * @param {string} bg
   * @returns {string}
   */
  function nexodeAirTabLabelColorForBg(bg) {
    if (!bg || typeof bg !== 'string') return '#ffffff';
    const s = bg.trim();
    let r = 133;
    let g = 134;
    let b = 148;
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
    if (hex) {
      let h = hex[1];
      if (h.length === 3) {
        h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      }
      r = parseInt(h.slice(0, 2), 16);
      g = parseInt(h.slice(2, 4), 16);
      b = parseInt(h.slice(4, 6), 16);
    } else if (/^rgba?\(/i.test(s)) {
      const m = s.match(/\d+(\.\d+)?/g);
      if (m && m.length >= 3) {
        r = Math.min(255, Math.round(Number(m[0])));
        g = Math.min(255, Math.round(Number(m[1])));
        b = Math.min(255, Math.round(Number(m[2])));
      }
    }
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.62 ? '#191a1a' : '#ffffff';
  }

  function buildNexodeAirTemplateFrameUrl(prefix, index, pad, ext) {
    return prefix + String(index).padStart(pad, '0') + '.' + ext;
  }

  /**
   * Highlight：`[data-na-highlight]` 内拆字后逐字 GSAP 入场；整段一条横向渐变，由 `--na-hl-grad-w` 与每字 `--na-hl-grad-x` 对齐；
   * resize / ResizeObserver / ScrollTrigger.refresh 时重算偏移，避免换行后渐变错位（与 nexode-air.js 同源，类名前缀为 template）。
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; registered: unknown[]; reduced: boolean }} ctx
   */
  function initWhenNexode65wHighlight(root, ctx) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const { cleanups, registered, reduced } = ctx;

    root.querySelectorAll('[data-na-highlight]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const text = section.querySelector('[data-na-highlight-text]');
      if (!(text instanceof HTMLElement)) return;
      const wrap = text.querySelector('.ug-nexode-air-template__gradient-text-wrap');
      if (!(wrap instanceof HTMLElement)) return;

      const baseEl = wrap.querySelector('.ug-nexode-air-template__gradient-text-base');
      if (!(baseEl instanceof HTMLElement)) return;
      const fillEl = wrap.querySelector('.ug-nexode-air-template__gradient-text-fill');
      if (!(fillEl instanceof HTMLElement)) return;

      const plain = (baseEl.textContent || '').trim();
      if (!plain) return;

      if (!gsap || !ScrollTrigger || reduced) {
        setNexodeAirTemplateTextReveal(text, reduced ? 100 : 0);
        text.style.opacity = '1';
        text.style.transform = 'none';
        return;
      }

      const savedWrapHtml = wrap.innerHTML;

      /**
       * 拆字：连续非空白为 `.ug-nexode-air-template__highlight-word`（nowrap），空格保留为文本节点。
       */
      const mountHighlightCharPairs = () => {
        wrap.innerHTML = '';
        const out = [];
        let i = 0;
        while (i < plain.length) {
          const ch = plain[i];
          if (ch === '\n' || ch === '\r') {
            i += 1;
            continue;
          }
          if (/\s/.test(ch)) {
            wrap.appendChild(document.createTextNode(ch === '\t' ? ' ' : ch));
            i += 1;
            continue;
          }
          const wordEl = document.createElement('span');
          wordEl.className = 'ug-nexode-air-template__highlight-word';
          while (i < plain.length && !/\s/.test(plain[i])) {
            const c = plain[i];
            const pair = document.createElement('span');
            pair.className = 'ug-nexode-air-template__highlight-char-pair';
            const b = document.createElement('span');
            b.className = 'ug-nexode-air-template__gradient-text-base ug-nexode-air-template__highlight-char-base';
            b.textContent = c;
            const f = document.createElement('span');
            f.className = 'ug-nexode-air-template__gradient-text-fill ug-nexode-air-template__highlight-char-fill';
            f.setAttribute('aria-hidden', 'true');
            f.textContent = c;
            pair.appendChild(b);
            pair.appendChild(f);
            wordEl.appendChild(pair);
            out.push(pair);
            i += 1;
          }
          if (wordEl.childElementCount > 0) {
            wrap.appendChild(wordEl);
          }
        }
        return out;
      };

      const pairs = mountHighlightCharPairs();
      if (!pairs.length) {
        wrap.innerHTML = savedWrapHtml;
        return;
      }

      const applyHighlightUnifiedGradientShifts = () => {
        if (!pairs.length) return;
        const wrapRect = wrap.getBoundingClientRect();
        const totalW = Math.max(1, Math.round(wrapRect.width));
        wrap.style.setProperty('--na-hl-grad-w', `${totalW}px`);
        pairs.forEach((pair) => {
          if (pair instanceof HTMLElement) {
            const pr = pair.getBoundingClientRect();
            const x = pr.left - wrapRect.left;
            pair.style.setProperty('--na-hl-grad-x', `${-Math.round(x)}px`);
          }
        });
      };

      applyHighlightUnifiedGradientShifts();
      window.requestAnimationFrame(() => {
        applyHighlightUnifiedGradientShifts();
        window.requestAnimationFrame(applyHighlightUnifiedGradientShifts);
      });
      const onHlGradResize = () => applyHighlightUnifiedGradientShifts();
      window.addEventListener('resize', onHlGradResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onHlGradResize));
      let hlGradRo = null;
      if (typeof ResizeObserver === 'function') {
        hlGradRo = new ResizeObserver(() => applyHighlightUnifiedGradientShifts());
        hlGradRo.observe(wrap);
        cleanups.push(() => {
          try {
            hlGradRo.disconnect();
          } catch (_) {}
        });
      }

      const restoreWrap = () => {
        wrap.innerHTML = savedWrapHtml;
      };
      cleanups.push(restoreWrap);

      gsap.set(pairs, { y: '-0.85em', autoAlpha: 0, transformOrigin: '50% 0%' });
      // 与原先实现一致：toggleActions 进场播放；once 避免反复 reset。勿在 onRefresh 里用极小 progress 阈值 complete，否则首次 refresh 会误杀动画。
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: NA_SCROLL_REVEAL_START,
          toggleActions: 'play none none none',
          once: true
        }
      });
      tl.fromTo(
        pairs,
        { y: '-0.85em', autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.68,
          ease: 'back.out(1.35)',
          stagger: {
            each: 0.038,
            from: 'start',
            ease: 'sine.inOut'
          },
          onComplete: () => {
            applyHighlightUnifiedGradientShifts();
          }
        }
      );

      if (tl.scrollTrigger) {
        registered.push(tl.scrollTrigger);
      }

      // 进入页时已滚过 start 时 toggleActions 不会触发 play：仅在「滚动进度已进入区间且时间线仍停在起点」时补播；已滚过整段则直接落终态。
      const syncHighlightEnterIfAlreadyPast = () => {
        try {
          const st = tl.scrollTrigger;
          if (!st) return;
          st.refresh();
          const sp = typeof st.progress === 'number' ? st.progress : 0;
          const tp = typeof tl.totalProgress === 'function' ? tl.totalProgress() : 0;
          if (tp > 0.02) return;
          if (sp >= 0.999) {
            tl.progress(1);
            applyHighlightUnifiedGradientShifts();
          } else if (sp > 0.02) {
            tl.play(0);
          }
        } catch (_) {}
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(syncHighlightEnterIfAlreadyPast);
      });

      if (ScrollTrigger && typeof ScrollTrigger.addEventListener === 'function') {
        ScrollTrigger.addEventListener('refresh', applyHighlightUnifiedGradientShifts);
        cleanups.push(() => {
          ScrollTrigger.removeEventListener('refresh', applyHighlightUnifiedGradientShifts);
        });
      }
    });
  }

  /**
   * Air65 帧动画与文案：`data-na-air65`，`dataset.framePrefix` / `frameCount` / `framePad` / `frameExt`，可选 `data-air65-mo-video`。
   *
   * 桌面：section 顶贴视口顶开始 pin 内层 `.air65-pin`；进度前半段文案 ease 淡出 + 帧 0…AIR65_PIN_LAST_FRAME_INDEX，
   * 解 pin 后第二段 ScrollTrigger 仅推进剩余帧（文案保持淡出态）。`AIR65_SCRUB` + `AIR65_FRAME_EPS` 保证两段衔接不闪帧。
   * 上滚时第二段若 scrollY 回到 pin 区间内则改由第一段驱动，避免脱节。
   *
   * 手机：有 mo 视频则只播视频 + `setupAir65CopyEnter`；无视频只加载首帧 + 文案，无 pin。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; registered: unknown[]; reduced: boolean }} ctx
   */
  function initWhenNexode65wAir65(root, ctx) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const { cleanups, registered, reduced } = ctx;

    root.querySelectorAll('[data-na-air65]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const pin = section.querySelector('.ug-nexode-air-template__air65-pin');
      const stage = section.querySelector('.ug-nexode-air-template__air65-stage');
      const copy = section.querySelector('[data-na-air65-copy]');
      const eyebrow = section.querySelector('[data-na-air65-eyebrow]');
      const canvas = section.querySelector('[data-na-air65-canvas]');
      const poster = section.querySelector('[data-na-air65-poster]');
      const moVideoEl = section.querySelector('[data-na-air65-mo-video]');
      const prefix = section.dataset.framePrefix || '';
      const count = Number.parseInt(section.dataset.frameCount || '0', 10);
      const padW = Number.parseInt(section.dataset.framePad || '3', 10);
      const ext = section.dataset.frameExt || 'webp';
      const copyLineSelector =
        '.ug-nexode-air-template__air65-eyebrow, .ug-nexode-air-template__air65-title, .ug-nexode-air-template__air65-desc';
      const copyLines =
        copy instanceof HTMLElement
          ? Array.from(copy.querySelectorAll(copyLineSelector)).filter((el) => el instanceof HTMLElement)
          : [];

      const isAir65Mobile =
        typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;
      const moVideoUrl = (section.dataset.air65MoVideo || '').trim();

      const setupAir65CopyEnter = () => {
        if (!gsap) return;
        if (copy instanceof HTMLElement) {
          gsap.set(copy, { autoAlpha: 1, y: 0 });
        }
        if (copyLines.length) {
          gsap.set(copyLines, { autoAlpha: 0, y: 28 });
          const enterTl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: NA_SCROLL_REVEAL_START,
              once: true
            }
          });
          enterTl.to(copyLines, {
            autoAlpha: 1,
            y: 0,
            duration: 0.92,
            stagger: 0.22,
            ease: 'power2.out'
          });
          if (eyebrow instanceof HTMLElement && !reduced) {
            enterTl.to(
              eyebrow,
              {
                '--na-text-reveal': '100%',
                duration: 1.15,
                ease: 'power2.inOut'
              },
              '<'
            );
          }
          if (enterTl.scrollTrigger) {
            registered.push(enterTl.scrollTrigger);
          }
        }
      };

      if (isAir65Mobile && moVideoUrl && moVideoEl instanceof HTMLVideoElement) {
        if (!(pin instanceof HTMLElement) || !(stage instanceof HTMLElement)) return;

        moVideoEl.loop = false;
        naPrimeInlineVideoForAutoplay(moVideoEl);
        try {
          moVideoEl.pause();
          moVideoEl.currentTime = 0;
        } catch (_) {}

        /* 无 GSAP 时：进视口后 CSS 淡入再 play（与 UGREEN air 一致） */
        const setupMoVideoIoFallback = () => {
          moVideoEl.classList.add('ug-nexode-air-template__air65-mo-video--io-reveal');
          if (typeof IntersectionObserver === 'undefined') {
            moVideoEl.classList.add('is-visible');
            window.setTimeout(() => naTryPlayInlineVideo(moVideoEl), 480);
            return;
          }
          const io = new IntersectionObserver(
            (entries) => {
              entries.forEach((ent) => {
                if (!ent.isIntersecting) return;
                io.disconnect();
                moVideoEl.classList.add('is-visible');
                window.setTimeout(() => naTryPlayInlineVideo(moVideoEl), 500);
              });
            },
            { threshold: 0.14, rootMargin: '0px 0px -6% 0px' }
          );
          io.observe(moVideoEl);
          cleanups.push(() => {
            try {
              io.disconnect();
            } catch (_) {}
          });
        };

        if (eyebrow instanceof HTMLElement) {
          setNexodeAirTemplateTextReveal(eyebrow, reduced ? 100 : 0);
        }

        if (gsap && ScrollTrigger) {
          gsap.set(moVideoEl, { autoAlpha: 0 });
          const fadeDur = reduced ? 0 : 0.58;
          const videoEnterTl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: NA_SCROLL_REVEAL_START,
              once: true
            }
          });
          videoEnterTl.to(moVideoEl, {
            autoAlpha: 1,
            duration: fadeDur,
            ease: 'power2.out'
          });
          videoEnterTl.add(() => naTryPlayInlineVideo(moVideoEl));
          if (videoEnterTl.scrollTrigger) {
            registered.push(videoEnterTl.scrollTrigger);
          }
        } else {
          setupMoVideoIoFallback();
        }

        if (!gsap || !ScrollTrigger || reduced) {
          copyLines.forEach((el) => {
            el.style.opacity = '1';
            el.style.visibility = 'visible';
            el.style.transform = 'none';
          });
          if (copy instanceof HTMLElement) {
            copy.style.opacity = '';
            copy.style.visibility = '';
            copy.style.transform = '';
          }
          if (eyebrow instanceof HTMLElement) {
            setNexodeAirTemplateTextReveal(eyebrow, 100);
          }
        } else {
          setupAir65CopyEnter();
        }
        return;
      }

      if (
        !(pin instanceof HTMLElement) ||
        !(stage instanceof HTMLElement) ||
        !(canvas instanceof HTMLCanvasElement) ||
        !prefix ||
        !count
      ) {
        return;
      }

      const ctx2d = canvas.getContext('2d');
      if (!ctx2d) return;

      const isAir65MobileFramesOnly = isAir65Mobile && !moVideoUrl;
      const frameLoadCount = isAir65MobileFramesOnly ? 1 : count;
      const highPriorityFrameCount = isAir65Mobile
        ? AIR65_PRELOAD_HIGH_COUNT_MOBILE
        : AIR65_PRELOAD_HIGH_COUNT_DESKTOP;

      const frames = [];
      const imageSequence = { frame: 0 };

      const resolveRenderableFrame = (targetIndex) => {
        const safeIndex = Math.min(count - 1, Math.max(0, targetIndex));
        const direct = frames[safeIndex];
        if (direct && direct.complete && direct.naturalWidth) {
          return direct;
        }
        for (let prev = safeIndex - 1; prev >= 0; prev -= 1) {
          const prevFrame = frames[prev];
          if (prevFrame && prevFrame.complete && prevFrame.naturalWidth) {
            return prevFrame;
          }
        }
        for (let next = safeIndex + 1; next < count; next += 1) {
          const nextFrame = frames[next];
          if (nextFrame && nextFrame.complete && nextFrame.naturalWidth) {
            return nextFrame;
          }
        }
        return null;
      };

      const applyCanvasBufferSize = () => {
        const w = stage.offsetWidth;
        const h = stage.offsetHeight;
        if (w <= 0 || h <= 0) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const bw = Math.max(1, Math.round(w * dpr));
        const bh = Math.max(1, Math.round(h * dpr));
        if (canvas.width !== bw || canvas.height !== bh) {
          canvas.width = bw;
          canvas.height = bh;
        }
      };

      const renderCanvas = () => {
        const frameIndex = Math.min(count - 1, Math.max(0, Math.round(imageSequence.frame)));
        const frame = resolveRenderableFrame(frameIndex);
        if (!frame) return;
        applyCanvasBufferSize();
        const ctx = ctx2d;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const scale = Math.max(canvas.width / frame.naturalWidth, canvas.height / frame.naturalHeight);
        const drawWidth = frame.naturalWidth * scale;
        const drawHeight = frame.naturalHeight * scale;
        const drawX = (canvas.width - drawWidth) / 2;
        const drawY = (canvas.height - drawHeight) / 2;
        ctx.drawImage(frame, drawX, drawY, drawWidth, drawHeight);
        if (poster instanceof HTMLElement) {
          poster.classList.add('is-hidden');
        }
      };

      const renderFirstFrame = () => {
        imageSequence.frame = 0;
        renderCanvas();
      };

      const resizeCanvas = () => {
        renderCanvas();
      };

      for (let i = 0; i < frameLoadCount; i += 1) {
        const img = new Image();
        img.decoding = 'async';
        if ('fetchPriority' in img) {
          img.fetchPriority = i < highPriorityFrameCount ? 'high' : 'low';
        }
        if (i === 0) {
          img.onload = function () {
            resizeCanvas();
            renderFirstFrame();
            scheduleNexodeAirTemplateScrollTriggerRefresh();
          };
        } else {
          img.onload = () => {
            const cur = Math.round(imageSequence.frame);
            if (Math.abs(i - cur) <= 1) {
              renderCanvas();
            }
          };
        }
        img.src = buildNexodeAirTemplateFrameUrl(prefix, i + 1, padW, ext);
        frames[i] = img;
      }

      resizeCanvas();
      renderFirstFrame();

      const onAir65WindowResize = () => {
        resizeCanvas();
        renderCanvas();
      };
      window.addEventListener('resize', onAir65WindowResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onAir65WindowResize));

      let ro = null;
      if (typeof ResizeObserver === 'function') {
        ro = new ResizeObserver(() => {
          resizeCanvas();
          renderCanvas();
        });
        ro.observe(stage);
        cleanups.push(() => {
          try {
            ro.disconnect();
          } catch (_) {}
        });
      }

      if (eyebrow instanceof HTMLElement) {
        setNexodeAirTemplateTextReveal(eyebrow, reduced ? 100 : 0);
      }

      if (!gsap || !ScrollTrigger || reduced) {
        copyLines.forEach((el) => {
          el.style.opacity = '1';
          el.style.visibility = 'visible';
          el.style.transform = 'none';
        });
        if (eyebrow instanceof HTMLElement) {
          setNexodeAirTemplateTextReveal(eyebrow, 100);
        }
        if (copy instanceof HTMLElement) {
          copy.style.opacity = '';
          copy.style.visibility = '';
          copy.style.transform = '';
        }
        renderFirstFrame();
        return;
      }

      if (isAir65MobileFramesOnly) {
        setupAir65CopyEnter();
        return;
      }

      setupAir65CopyEnter();

      const AIR65_COPY_EXIT_Y = -48;
      const easeCopy =
        typeof gsap.parseEase === 'function' ? gsap.parseEase('power2.inOut') : (p) => p;
      let lastRenderedFrameIndex = -1;

      const pinCapFrameIndex = Math.min(AIR65_PIN_LAST_FRAME_INDEX, count - 1);
      const phase2FrameCount = Math.max(0, count - 1 - pinCapFrameIndex);

      const getAir65ScrollBase = () => {
        const vh = Math.max(
          typeof window.innerHeight === 'number' ? window.innerHeight : 0,
          pin.offsetHeight || 0,
          stage.offsetHeight || 0,
          800
        );
        const hasCopyDesktop = copyLines.length > 0 && !isAir65Mobile;
        const copyPx = hasCopyDesktop ? Math.round(vh * 0.38) : 0;
        const frameMult = isAir65Mobile ? 1.65 : 1.65;
        const countFactor = Math.min(1.55, Math.max(1, count / 100));
        const framePxFull = Math.round(vh * frameMult * countFactor);
        return { vh, hasCopyDesktop, copyPx, framePxFull };
      };

      const applyAir65CopyAndFrame = (progress, m, frameIndexMaxInclusive) => {
        const safeProgress = Math.max(0, Math.min(1, progress));
        const { copyProgressEnd, hasCopyDesktop } = m;

        if (copy instanceof HTMLElement && hasCopyDesktop) {
          const rawCopyProgress = copyProgressEnd > 0 ? safeProgress / copyProgressEnd : 1;
          const easedCopyProgress = easeCopy(Math.max(0, Math.min(1, rawCopyProgress)));
          gsap.set(copy, {
            y: AIR65_COPY_EXIT_Y * easedCopyProgress,
            autoAlpha: 1 - easedCopyProgress
          });
        } else if (copy instanceof HTMLElement) {
          gsap.set(copy, { y: 0, autoAlpha: 1 });
          if (copyLines.length) {
            gsap.set(copyLines, { autoAlpha: 1 });
          }
        }

        const frameLinear =
          copyProgressEnd > 0 && hasCopyDesktop
            ? (safeProgress - copyProgressEnd) / (1 - copyProgressEnd)
            : safeProgress;
        const frameT = Math.max(0, Math.min(1, frameLinear));
        let nextFrameIndex;
        if (frameT <= AIR65_FRAME_EPS) {
          nextFrameIndex = 0;
        } else if (frameT >= 1 - AIR65_FRAME_EPS) {
          nextFrameIndex = frameIndexMaxInclusive;
        } else {
          nextFrameIndex = Math.min(
            frameIndexMaxInclusive,
            Math.max(0, Math.round(frameIndexMaxInclusive * frameT))
          );
        }
        if (nextFrameIndex !== lastRenderedFrameIndex) {
          lastRenderedFrameIndex = nextFrameIndex;
          imageSequence.frame = nextFrameIndex;
          renderCanvas();
        }
      };

      const applyAir65Phase2FramesOnly = (progress) => {
        const safeProgress = Math.max(0, Math.min(1, progress));
        const { hasCopyDesktop } = getAir65ScrollBase();
        if (copy instanceof HTMLElement && hasCopyDesktop) {
          gsap.set(copy, { y: AIR65_COPY_EXIT_Y, autoAlpha: 0 });
        } else if (copy instanceof HTMLElement) {
          gsap.set(copy, { y: 0, autoAlpha: 1 });
          if (copyLines.length) {
            gsap.set(copyLines, { autoAlpha: 1 });
          }
        }
        let nextFrameIndex;
        if (safeProgress <= AIR65_FRAME_EPS) {
          nextFrameIndex = pinCapFrameIndex;
        } else if (safeProgress >= 1 - AIR65_FRAME_EPS) {
          nextFrameIndex = pinCapFrameIndex + phase2FrameCount;
        } else {
          nextFrameIndex = pinCapFrameIndex + Math.round(phase2FrameCount * safeProgress);
        }
        if (nextFrameIndex !== lastRenderedFrameIndex) {
          lastRenderedFrameIndex = nextFrameIndex;
          imageSequence.frame = nextFrameIndex;
          renderCanvas();
        }
      };

      if (phase2FrameCount > 0) {
        const getPhase1Metrics = () => {
          const { vh, hasCopyDesktop, copyPx, framePxFull } = getAir65ScrollBase();
          const framePx1 = Math.round((framePxFull * (pinCapFrameIndex + 1)) / count);
          const totalPx = Math.max(copyPx + framePx1, Math.round(vh * 1.25));
          const copyProgressEnd = totalPx > 0 && hasCopyDesktop ? copyPx / totalPx : 0;
          return { totalPx, copyProgressEnd, hasCopyDesktop };
        };

        const getPhase2Px = () => {
          const { framePxFull } = getAir65ScrollBase();
          return Math.round((framePxFull * phase2FrameCount) / count);
        };

        const runPhase1 = (self) => {
          applyAir65CopyAndFrame(self.progress, getPhase1Metrics(), pinCapFrameIndex);
        };

        const air65PinTrigger = ScrollTrigger.create({
          trigger: section,
          start: 'top top',
          end: () => '+=' + getPhase1Metrics().totalPx,
          pin,
          pinSpacing: true,
          scrub: AIR65_SCRUB,
          anticipatePin: 0,
          invalidateOnRefresh: true,
          onUpdate: runPhase1,
          onRefreshInit: resizeCanvas,
          onRefresh: (self) => {
            renderCanvas();
            runPhase1(self);
          }
        });

        const runPhase2 = (self) => {
          const sy = window.scrollY || window.pageYOffset || 0;
          if (air65PinTrigger && sy < air65PinTrigger.end - 0.5) {
            runPhase1(air65PinTrigger);
            return;
          }
          applyAir65Phase2FramesOnly(self.progress);
        };

        const air65TailTrigger = ScrollTrigger.create({
          trigger: section,
          start: () => (air65PinTrigger.end || 0),
          end: () => '+=' + getPhase2Px(),
          scrub: AIR65_SCRUB,
          anticipatePin: 0,
          invalidateOnRefresh: true,
          onUpdate: runPhase2,
          onRefreshInit: resizeCanvas,
          onRefresh: (self) => {
            renderCanvas();
            runPhase2(self);
          }
        });

        runPhase1({ progress: 0 });
        window.requestAnimationFrame(() => {
          renderCanvas();
          if (air65PinTrigger) {
            runPhase1(air65PinTrigger);
          }
          if (air65TailTrigger && air65PinTrigger && air65PinTrigger.progress >= 1) {
            runPhase2(air65TailTrigger);
          }
        });
        if (air65PinTrigger) registered.push(air65PinTrigger);
        if (air65TailTrigger) registered.push(air65TailTrigger);
      } else {
        const getAir65ScrollMetrics = () => {
          const { vh, hasCopyDesktop, copyPx, framePxFull } = getAir65ScrollBase();
          const totalPx = Math.max(copyPx + framePxFull, Math.round(vh * 1.25));
          const copyProgressEnd = totalPx > 0 && hasCopyDesktop ? copyPx / totalPx : 0;
          return { totalPx, copyProgressEnd, hasCopyDesktop };
        };

        const updateAir65State = (progress) => {
          applyAir65CopyAndFrame(progress, getAir65ScrollMetrics(), count - 1);
        };

        updateAir65State(0);

        const air65Trigger = ScrollTrigger.create({
          trigger: section,
          start: 'top top',
          end: () => '+=' + getAir65ScrollMetrics().totalPx,
          pin,
          pinSpacing: true,
          scrub: AIR65_SCRUB,
          anticipatePin: 0,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            updateAir65State(self.progress);
          },
          onRefreshInit: resizeCanvas,
          onRefresh: (self) => {
            renderCanvas();
            updateAir65State(self.progress);
          }
        });

        window.requestAnimationFrame(() => {
          updateAir65State(air65Trigger ? air65Trigger.progress || 0 : 0);
        });
        if (air65Trigger) {
          registered.push(air65Trigger);
        }
      }
    });
  }

  /**
   * Colors（`data-na-colors`）：Tab 指示器随滚动/resize 同步；入场时间线含眉题 `--na-text-reveal`。
   * Tab 切换时成对 gallery 做 z-index 叠层、左图 clipPath 向中心收缩（SWITCH_MS）、右图与 pair 同步显隐；`colorSwitchTokenBySection` / `colorAnimAbortBySection` 防连点与上一轮动画重叠。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; registered: unknown[]; reduced: boolean }} ctx
   */
  function initNexodeAirTemplateColorCompat(root, ctx) {
    const { cleanups, registered, reduced } = ctx;
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const colorSwitchTokenBySection = new WeakMap();
    const colorAnimAbortBySection = new WeakMap();

    root.querySelectorAll('[data-na-colors]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const pairs = section.querySelectorAll('[data-na-colors-pair]');
      const tabs = section.querySelectorAll('[data-na-colors-tab]');
      const tabsContainer = section.querySelector('.ug-nexode-air-template__colors-tabs');
      const indicator = section.querySelector('[data-na-colors-indicator]');
      const prevBtn = section.querySelector('[data-na-colors-prev]');
      const nextBtn = section.querySelector('[data-na-colors-next]');
      const eyebrow = section.querySelector('[data-na-colors-eyebrow]');
      const tabsOuter = section.querySelector('[data-na-colors-tabs-outer]');
      const titleLines = section.querySelectorAll('[data-na-colors-title-line]');

      if (!tabs.length || !pairs.length) {
        return;
      }
      if (pairs.length !== tabs.length) {
        return;
      }

      const getIsMobile = () =>
        typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;

      const resolveTabActiveBg = (btn) => {
        if (!(btn instanceof HTMLElement)) return '#858694';
        return (btn.dataset.activeBg || '').trim() || '#858694';
      };

      const resolveTabActiveFg = (btn) => {
        if (!(btn instanceof HTMLElement)) return '#ffffff';
        const fg = (btn.dataset.activeColor || '').trim();
        if (fg) return fg;
        return nexodeAirTabLabelColorForBg(resolveTabActiveBg(btn));
      };

      const applyTabVisual = (activeBtn) => {
        tabs.forEach((t) => {
          if (!(t instanceof HTMLElement)) return;
          const active = t === activeBtn;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          const bg = resolveTabActiveBg(t);
          if (active) {
            t.style.backgroundColor = '';
            t.style.color = resolveTabActiveFg(t);
          } else {
            t.style.backgroundColor = '';
            t.style.color = '';
          }
        });
        if (indicator instanceof HTMLElement && activeBtn instanceof HTMLElement) {
          indicator.style.backgroundColor = resolveTabActiveBg(activeBtn);
        }
      };

      const syncIndicator = (activeBtn, animate) => {
        if (!(tabsContainer instanceof HTMLElement) || !(indicator instanceof HTMLElement) || !(activeBtn instanceof HTMLElement)) {
          return;
        }
        const p = tabsContainer.getBoundingClientRect();
        const b = activeBtn.getBoundingClientRect();
        const left = b.left - p.left + tabsContainer.scrollLeft;
        const rawTop = b.top - p.top + tabsContainer.scrollTop;
        const top = rawTop - 1;
        const w = b.width;
        const h = b.height;
        if (animate && gsap && !reduced) {
          gsap.to(indicator, {
            left: left,
            top: top,
            width: w,
            height: h,
            duration: 0.4,
            ease: 'power3.out'
          });
        } else if (gsap) {
          gsap.set(indicator, { left: left, top: top, width: w, height: h });
        } else {
          indicator.style.left = left + 'px';
          indicator.style.top = top + 'px';
          indicator.style.width = w + 'px';
          indicator.style.height = h + 'px';
        }
      };

      const scheduleIndicatorSync = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active') || tabs[0];
        if (cur instanceof HTMLElement) {
          syncIndicator(cur, false);
        }
      };

      const getActiveTabIndex = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active');
        const idx = cur ? Array.prototype.indexOf.call(tabs, cur) : 0;
        return Math.max(0, Math.min(tabs.length - 1, idx));
      };

      const updateNavButtons = () => {
        const showNav = tabs.length > 1;
        const activeIdx = getActiveTabIndex();
        const showPrev = showNav && activeIdx > 0;
        const showNext = showNav && activeIdx < tabs.length - 1;
        if (prevBtn instanceof HTMLButtonElement) {
          prevBtn.hidden = !showPrev;
        }
        if (nextBtn instanceof HTMLButtonElement) {
          nextBtn.hidden = !showNext;
        }
      };

      const syncColorsTabsUi = (activeBtn, animate) => {
        updateNavButtons();
        const targetBtn =
          activeBtn instanceof HTMLElement
            ? activeBtn
            : section.querySelector('[data-na-colors-tab].is-active') || tabs[0];
        window.requestAnimationFrame(() => {
          if (targetBtn instanceof HTMLElement) {
            syncIndicator(targetBtn, !!animate);
          }
        });
      };

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          syncColorsTabsUi(activeTab instanceof HTMLElement ? activeTab : tabs[0], false);
        });
      });

      const activeTab = section.querySelector('[data-na-colors-tab].is-active') || tabs[0];
      if (activeTab instanceof HTMLElement) {
        applyTabVisual(activeTab);
      }
      syncColorsTabsUi(activeTab instanceof HTMLElement ? activeTab : tabs[0], false);

      const onTabsScroll = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active');
        if (cur instanceof HTMLElement) {
          syncIndicator(cur, false);
        }
      };
      if (tabsContainer instanceof HTMLElement) {
        tabsContainer.addEventListener('scroll', onTabsScroll, { passive: true });
        cleanups.push(() => tabsContainer.removeEventListener('scroll', onTabsScroll));
      }

      let tabRo = null;
      if (typeof ResizeObserver !== 'undefined' && tabsContainer instanceof HTMLElement) {
        tabRo = new ResizeObserver(() => {
          const cur = section.querySelector('[data-na-colors-tab].is-active');
          if (cur instanceof HTMLElement) {
            syncIndicator(cur, false);
          }
        });
        tabRo.observe(tabsContainer);
        cleanups.push(() => {
          try {
            tabRo.disconnect();
          } catch (_) {}
        });
      }

      const onWinResize = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active');
        syncColorsTabsUi(cur instanceof HTMLElement ? cur : tabs[0], false);
        if (!colorsIntroPlayed && getIsMobile() !== lastIntroIsMobile) {
          resetIntroMediaState();
        }
      };
      window.addEventListener('resize', onWinResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onWinResize));

      const getActiveSlideElements = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active');
        const idx = cur ? Array.prototype.indexOf.call(tabs, cur) : 0;
        const safe = Math.max(0, Math.min(tabs.length - 1, idx));
        const pair = pairs[safe];
        if (!(pair instanceof HTMLElement)) {
          return { left: null, right: null };
        }
        const left = pair.querySelector('.ug-nexode-air-template__colors-img-crop--left');
        const right = pair.querySelector('.ug-nexode-air-template__colors-right-tile');
        return { left, right };
      };

      const setStaticFinal = () => {
        if (eyebrow instanceof HTMLElement) {
          setNexodeAirTemplateTextReveal(eyebrow, 100);
          eyebrow.style.opacity = '1';
          eyebrow.style.transform = 'none';
        }
        titleLines.forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.opacity = '1';
            el.style.transform = 'none';
          }
        });
        pairs.forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.opacity = '';
            el.style.transform = '';
            el.style.removeProperty('z-index');
          }
        });
        if (tabsOuter instanceof HTMLElement) {
          tabsOuter.style.opacity = '1';
          tabsOuter.style.transform = 'none';
        }
        scheduleIndicatorSync();
      };

      const runTabSwitchAnimation = (btn) => {
        let prevActive = section.querySelector('[data-na-colors-tab].is-active');
        if (!(prevActive instanceof HTMLElement)) {
          prevActive = tabs[0];
        }
        const fromIdx = Array.prototype.indexOf.call(tabs, prevActive);
        const toIdx = Array.prototype.indexOf.call(tabs, btn);
        if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) {
          return;
        }

        const outPair = pairs[fromIdx];
        const inPair = pairs[toIdx];
        if (!(outPair instanceof HTMLElement) || !(inPair instanceof HTMLElement)) {
          return;
        }

        const outLeft = outPair.querySelector('.ug-nexode-air-template__colors-img-crop--left');
        const outRight = outPair.querySelector('.ug-nexode-air-template__colors-right-tile');
        const inLeft = inPair.querySelector('.ug-nexode-air-template__colors-img-crop--left');
        const inRight = inPair.querySelector('.ug-nexode-air-template__colors-right-tile');
        if (
          !(outLeft instanceof HTMLElement) ||
          !(outRight instanceof HTMLElement) ||
          !(inLeft instanceof HTMLElement) ||
          !(inRight instanceof HTMLElement)
        ) {
          return;
        }

        applyTabVisual(btn);
        syncColorsTabsUi(btn, true);

        if (reduced || !gsap) {
          outPair.classList.remove('is-active', 'current');
          inPair.classList.add('is-active', 'current');
          outPair.setAttribute('aria-hidden', 'true');
          inPair.setAttribute('aria-hidden', 'false');
          return;
        }

        const prevAbort = colorAnimAbortBySection.get(section);
        if (typeof prevAbort === 'function') {
          try {
            prevAbort();
          } catch (_) {}
        }

        const switchToken = (colorSwitchTokenBySection.get(section) || 0) + 1;
        colorSwitchTokenBySection.set(section, switchToken);

        gsap.killTweensOf([outPair, inPair, outLeft, outRight, inLeft, inRight]);

        const SWITCH_MS = 800;
        const switchSec = SWITCH_MS / 1000;

        pairs.forEach((el) => {
          if (el instanceof HTMLElement) el.style.removeProperty('z-index');
        });
        outPair.style.zIndex = '3';
        inPair.style.zIndex = '2';
        outPair.classList.remove('is-active');
        outPair.classList.add('current');
        inPair.classList.add('is-active', 'current');
        inPair.setAttribute('aria-hidden', 'false');

        gsap.set(inPair, { autoAlpha: 1 });
        gsap.set(outPair, { autoAlpha: 1 });
        gsap.set(inLeft, { autoAlpha: 1 });
        gsap.set(inRight, { autoAlpha: 1 });
        gsap.set(outRight, { autoAlpha: 1 });

        const outLeftRect = outLeft.getBoundingClientRect();
        const targetSize = 0;
        const targetVisibleWidth = Math.min(targetSize, outLeftRect.width);
        const targetVisibleHeight = Math.min(targetSize, outLeftRect.height);
        const maxInsetX = Math.max(0, (outLeftRect.width - targetVisibleWidth) / 2);
        const maxInsetY = Math.max(0, (outLeftRect.height - targetVisibleHeight) / 2);
        const clipState = { progress: 0 };
        const OUT_LEFT_FADE_START_W = 80;

        const applyOutLeftClip = (progress) => {
          const safe = Math.max(0, Math.min(1, progress));
          const insetX = maxInsetX * safe;
          const insetY = maxInsetY * safe;
          const clipValue =
            'inset(' +
            insetY.toFixed(3) +
            'px ' +
            insetX.toFixed(3) +
            'px ' +
            insetY.toFixed(3) +
            'px ' +
            insetX.toFixed(3) +
            'px round 30px)';
          outLeft.style.clipPath = clipValue;
          outLeft.style.webkitClipPath = clipValue;
          const visibleW = Math.max(0, outLeftRect.width - 2 * insetX);
          const fadeOp =
            visibleW >= OUT_LEFT_FADE_START_W ? 1 : Math.max(0, visibleW / OUT_LEFT_FADE_START_W);
          gsap.set(outLeft, { opacity: fadeOp });
        };

        applyOutLeftClip(0);

        const finalizePairClasses = () => {
          outPair.classList.remove('is-active', 'current');
          inPair.classList.add('is-active', 'current');
          outPair.setAttribute('aria-hidden', 'true');
          inPair.setAttribute('aria-hidden', 'false');
        };

        const clearPairAnimStyles = () => {
          try {
            gsap.set([outPair, inPair, outLeft, outRight, inLeft, inRight], { clearProps: 'opacity,visibility,transform' });
          } catch (_) {}
          outLeft.style.clipPath = '';
          outLeft.style.webkitClipPath = '';
          outPair.style.removeProperty('z-index');
          inPair.style.removeProperty('z-index');
        };

        const tl = gsap.timeline({
          onComplete: () => {
            if (colorSwitchTokenBySection.get(section) !== switchToken) {
              return;
            }
            colorAnimAbortBySection.delete(section);
            finalizePairClasses();
            clearPairAnimStyles();
          }
        });

        tl.to(
          clipState,
          {
            duration: switchSec,
            progress: 1,
            ease: 'power2.inOut',
            onUpdate: () => {
              applyOutLeftClip(clipState.progress);
            }
          },
          0
        );
        tl.fromTo(
          inRight,
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration: switchSec,
            ease: 'power2.out'
          },
          0
        );
        tl.to(
          outRight,
          {
            autoAlpha: 0,
            duration: switchSec,
            ease: 'power2.inOut'
          },
          0
        );

        colorAnimAbortBySection.set(section, () => {
          try {
            tl.kill();
          } catch (_) {}
          try {
            gsap.killTweensOf([outPair, inPair, outLeft, outRight, inLeft, inRight, clipState]);
          } catch (_) {}
          finalizePairClasses();
          clearPairAnimStyles();
          colorAnimAbortBySection.delete(section);
        });
      };

      const onTabClick = (e) => {
        const btn = e.currentTarget;
        if (!(btn instanceof HTMLElement)) return;
        if (btn.classList.contains('is-active')) return;
        runTabSwitchAnimation(btn);
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', onTabClick);
        cleanups.push(() => tab.removeEventListener('click', onTabClick));
      });

      if (prevBtn instanceof HTMLButtonElement) {
        const onPrevClick = () => {
          const activeIdx = getActiveTabIndex();
          const prevIdx = Math.max(0, activeIdx - 1);
          const prevTab = tabs[prevIdx];
          if (!(prevTab instanceof HTMLElement)) return;
          if (!prevTab.classList.contains('is-active')) {
            runTabSwitchAnimation(prevTab);
          }
          if (typeof prevTab.scrollIntoView === 'function') {
            prevTab.scrollIntoView({
              behavior: reduced || getIsMobile() ? 'auto' : 'smooth',
              inline: 'center',
              block: 'nearest'
            });
          }
        };
        prevBtn.addEventListener('click', onPrevClick);
        cleanups.push(() => prevBtn.removeEventListener('click', onPrevClick));
      }

      if (nextBtn instanceof HTMLButtonElement) {
        const onNextClick = () => {
          const activeIdx = getActiveTabIndex();
          const nextIdx = Math.min(tabs.length - 1, activeIdx + 1);
          const nextTab = tabs[nextIdx];
          if (!(nextTab instanceof HTMLElement)) return;
          if (!nextTab.classList.contains('is-active')) {
            runTabSwitchAnimation(nextTab);
          }
          if (typeof nextTab.scrollIntoView === 'function') {
            nextTab.scrollIntoView({
              behavior: reduced || getIsMobile() ? 'auto' : 'smooth',
              inline: 'center',
              block: 'nearest'
            });
          }
        };
        nextBtn.addEventListener('click', onNextClick);
        cleanups.push(() => nextBtn.removeEventListener('click', onNextClick));
      }

      if (reduced || !gsap || !ScrollTrigger) {
        setStaticFinal();
        return;
      }

      const pairEls = getActiveSlideElements();
      const left = pairEls.left;
      const right = pairEls.right;
      const hasPairMedia = left instanceof HTMLElement && right instanceof HTMLElement;
      let colorsIntroPlayed = false;
      let lastIntroIsMobile = getIsMobile();

      const resetIntroMediaState = () => {
        if (!hasPairMedia || !gsap || colorsIntroPlayed) return;
        const nowMobile = getIsMobile();
        lastIntroIsMobile = nowMobile;
        gsap.set([left, right], { clearProps: 'x,y' });
        if (nowMobile) {
          gsap.set(left, { y: -36, autoAlpha: 0 });
          gsap.set(right, { y: 36, autoAlpha: 0 });
        } else {
          gsap.set(left, { x: -72, autoAlpha: 0 });
          gsap.set(right, { x: 72, autoAlpha: 0 });
        }
      };

      const introMm = ScrollTrigger.matchMedia({
        [MOBILE_MQ]: () => {
          /* 小屏：块内眉题/图/Tab 不再逐项渐显，统一由 `colors-inner` 的滚动渐显整包淡入 */
          setStaticFinal();
          colorsIntroPlayed = true;
          return () => {};
        },
        '(min-width: 1024px)': () => {
          colorsIntroPlayed = false;

          setNexodeAirTemplateTextReveal(eyebrow instanceof HTMLElement ? eyebrow : null, 0);
          if (eyebrow) {
            gsap.set(eyebrow, { autoAlpha: 0, y: 20 });
          }
          if (titleLines.length) {
            gsap.set(titleLines, { autoAlpha: 0, y: 18 });
          }

          resetIntroMediaState();

          if (tabsOuter) {
            gsap.set(tabsOuter, { autoAlpha: 0, y: 14 });
          }

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: NA_SCROLL_REVEAL_START,
              toggleActions: 'play none none none',
              once: true,
              onEnter: () => {
                colorsIntroPlayed = true;
              }
            }
          });

          if (eyebrow) {
            tl.fromTo(
              eyebrow,
              { autoAlpha: 0, y: 20 },
              { autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out' }
            );
            tl.to(
              eyebrow,
              {
                '--na-text-reveal': '100%',
                duration: 1,
                ease: 'power2.inOut'
              },
              '-=0.15'
            );
          }

          if (titleLines.length) {
            tl.to(
              titleLines,
              {
                autoAlpha: 1,
                y: 0,
                duration: 0.5,
                stagger: 0.14,
                ease: 'power2.out'
              },
              eyebrow ? '-=0.35' : 0
            );
          }

          if (hasPairMedia) {
            tl.to(
              [left, right],
              {
                x: 0,
                autoAlpha: 1,
                duration: 0.7,
                stagger: 0.08,
                ease: 'power3.out'
              },
              titleLines.length ? '-=0.25' : eyebrow ? '-=0.2' : 0
            );
          }

          if (tabsOuter) {
            tl.to(
              tabsOuter,
              { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' },
              '-=0.15'
            );
          }

          if (tl.scrollTrigger) {
            registered.push(tl.scrollTrigger);
          }

          return () => {
            try {
              tl.kill();
            } catch (_) {}
            colorsIntroPlayed = false;
          };
        }
      });

      cleanups.push(() => {
        try {
          introMm.revert();
        } catch (_) {}
      });
    });
  }

  /**
   * Why Air：桌面滚动渐显，移动端直接全显；中间切换卡支持自动轮播与点控。
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initNexodeAirWhyAir(root, ctx) {
    const { cleanups, reduced } = ctx;
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    /* 手机端直接全显，避免 scrub 区间不足导致中间卡不亮 */
    const isMobile =
      typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;

    root.querySelectorAll('[data-na-why-air]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;

      const orderedItems = Array.from(section.querySelectorAll('[data-na-why-air-item]'))
        .filter((el) => el instanceof HTMLElement)
        .sort((a, b) => {
          const ao = Number(a.getAttribute('data-na-why-air-order') || 0);
          const bo = Number(b.getAttribute('data-na-why-air-order') || 0);
          return ao - bo;
        });

      const applyVisibleCount = (count) => {
        orderedItems.forEach((el, idx) => {
          el.classList.toggle('is-visible', idx < count);
        });
      };

      if (!orderedItems.length) return;

      if (reduced || !gsap || !ScrollTrigger || isMobile) {
        applyVisibleCount(orderedItems.length);
      } else {
        /* 区块进入视口后逐张渐显 */
        applyVisibleCount(0);
        const st = ScrollTrigger.create({
          trigger: section,
          start: 'top 60%',
          end: 'center center',
          scrub: true,
          onUpdate: (self) => {
            const progress = Number(self?.progress || 0);
            const n = orderedItems.length;
            const count = Math.min(n, Math.ceil(progress * n));
            applyVisibleCount(count);
          },
          onLeaveBack: () => applyVisibleCount(0)
        });
        cleanups.push(() => {
          try {
            st.kill();
          } catch (_) {}
        });
      }

      const switchCard = section.querySelector('[data-na-why-air-switch]');
      if (!(switchCard instanceof HTMLElement)) return;

      const imgs = Array.from(switchCard.querySelectorAll('[data-na-why-air-switch-img]')).filter(
        (el) => el instanceof HTMLElement
      );
      const dots = Array.from(switchCard.querySelectorAll('[data-na-why-air-switch-dot]')).filter(
        (el) => el instanceof HTMLButtonElement
      );
      if (!imgs.length) return;

      let active = 0;
      let rafId = 0;
      let slideStartedAt = 0;
      const intervalMs = Math.max(1200, Number(switchCard.getAttribute('data-na-why-air-switch-interval') || 2800));

      /* 当前激活指示条进度（0-1） */
      const syncDotProgress = (p) => {
        const clamped = Math.max(0, Math.min(1, p));
        dots.forEach((dot, i) => {
          dot.style.setProperty('--na-fill', i === active ? String(clamped) : '0');
        });
      };

      const setActive = (idx) => {
        active = ((idx % imgs.length) + imgs.length) % imgs.length;
        imgs.forEach((el, i) => el.classList.toggle('is-active', i === active));
        dots.forEach((el, i) => el.classList.toggle('is-active', i === active));
        syncDotProgress(0);
      };

      const stopAuto = () => {
        if (rafId) {
          window.cancelAnimationFrame(rafId);
          rafId = 0;
        }
      };

      const tick = (now) => {
        rafId = window.requestAnimationFrame(tick);
        let elapsed = now - slideStartedAt;
        if (elapsed >= intervalMs) {
          slideStartedAt = now;
          setActive(active + 1);
          elapsed = 0;
        }
        syncDotProgress(elapsed / intervalMs);
      };

      const startAuto = () => {
        stopAuto();
        if (imgs.length < 2 || reduced) return;
        slideStartedAt = performance.now();
        rafId = window.requestAnimationFrame(tick);
      };

      dots.forEach((dot, idx) => {
        const onClick = (e) => {
          e.preventDefault();
          setActive(idx);
          startAuto();
        };
        dot.addEventListener('click', onClick);
        cleanups.push(() => dot.removeEventListener('click', onClick));
      });

      const onEnter = () => {
        if (imgs.length < 2) return;
        setActive(active + 1);
        stopAuto();
      };
      const onLeave = () => startAuto();
      switchCard.addEventListener('mouseenter', onEnter);
      switchCard.addEventListener('mouseleave', onLeave);
      cleanups.push(() => {
        switchCard.removeEventListener('mouseenter', onEnter);
        switchCard.removeEventListener('mouseleave', onLeave);
      });

      setActive(0);
      startAuto();
      cleanups.push(() => stopAuto());
    });
  }

  /**
   * TVC 视频弹窗：按钮读取 YouTube embed 链接，打开 modal 内 iframe 播放。
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initNexodeAirTvcModal(root, ctx) {
    const { cleanups } = ctx;
    root.querySelectorAll('[data-na-tvc]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;

      const trigger = section.querySelector('.js-na-tvc-video-trigger');
      const modal = section.querySelector('[data-na-tvc-modal]');
      const iframe = section.querySelector('[data-na-tvc-iframe]');
      if (!(trigger instanceof HTMLButtonElement) || !(modal instanceof HTMLElement) || !(iframe instanceof HTMLIFrameElement)) {
        return;
      }

      const closeBtns = Array.from(section.querySelectorAll('[data-na-tvc-modal-close]')).filter(
        (el) => el instanceof HTMLElement
      );

      const withAutoplay = (rawUrl) => {
        const url = String(rawUrl || '').trim();
        if (!url) return '';
        try {
          const u = new URL(url, window.location.origin);
          if (!u.searchParams.get('autoplay')) u.searchParams.set('autoplay', '1');
          if (!u.searchParams.get('rel')) u.searchParams.set('rel', '0');
          return u.toString();
        } catch (_) {
          return url + (url.includes('?') ? '&' : '?') + 'autoplay=1&rel=0';
        }
      };

      const openModal = () => {
        const videoUrl = trigger.getAttribute('data-na-tvc-video-url') || '';
        if (!videoUrl.trim()) return;
        iframe.src = withAutoplay(videoUrl);
        modal.removeAttribute('hidden');
        modal.setAttribute('aria-hidden', 'false');
      };

      const closeModal = () => {
        modal.setAttribute('hidden', '');
        modal.setAttribute('aria-hidden', 'true');
        iframe.src = '';
      };

      const onTrigger = (e) => {
        e.preventDefault();
        openModal();
      };
      trigger.addEventListener('click', onTrigger);
      cleanups.push(() => trigger.removeEventListener('click', onTrigger));

      closeBtns.forEach((btn) => {
        const onClose = (e) => {
          e.preventDefault();
          closeModal();
        };
        btn.addEventListener('click', onClose);
        cleanups.push(() => btn.removeEventListener('click', onClose));
      });

      const onEsc = (e) => {
        if (!(e instanceof KeyboardEvent)) return;
        if (e.key !== 'Escape') return;
        if (modal.hasAttribute('hidden')) return;
        closeModal();
      };
      document.addEventListener('keydown', onEsc);
      cleanups.push(() => document.removeEventListener('keydown', onEsc));
    });
  }

  /**
   * 旧 JSON 缺 `price_text` 时仅做数值兜底；展示货币一律以 Liquid `| money` 输出为准
   *
   * @param {number | string | null | undefined} cents
   * @returns {string}
   */
  function naEditionsFormatMoney(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    return String(n / 100);
  }

  /**
   * 解析系列卡内嵌 JSON，避免首字符空格或非法片段导致变体区整体失效。
   *
   * @param {HTMLScriptElement | null} scriptEl
   * @returns {any[]}
   */
  function naEditionsParseRows(scriptEl) {
    if (!(scriptEl instanceof HTMLScriptElement) || !scriptEl.textContent) return [];
    const raw = scriptEl.textContent.trim();
    if (!raw) return [];
    try {
      const out = JSON.parse(raw);
      return Array.isArray(out) ? out : [];
    } catch (_) {
      try {
        const arrStart = raw.indexOf('[');
        const arrEnd = raw.lastIndexOf(']');
        if (arrStart < 0 || arrEnd < arrStart) return [];
        return JSON.parse(raw.slice(arrStart, arrEnd + 1)) || [];
      } catch (__) {
        return [];
      }
    }
  }

  /**
   * 系列卡桌面端：对齐 `ug-index-section-ProductList` — Swiper 横滑，<4 张居中，>4 张显示左右导航；手机端不实例化、由 CSS 栅格负责。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initNexodeAirEditionsSwiper(root, ctx) {
    const { cleanups } = ctx;
    const section = root.querySelector('.ug-nexode-air-template__editions');
    const el = section && section.querySelector('[data-na-editions-swiper]');
    if (!section || !(el instanceof HTMLElement)) return;

    const navWrap = section.querySelector('[data-na-editions-nav]');
    const prev = section.querySelector('[data-na-editions-nav-prev]');
    const next = section.querySelector('[data-na-editions-nav-next]');
    const DESKTOP_MQ = '(min-width: 1024px)';
    let swiper = null;
    let resizeT = 0;
    let swiperWait = 0;

    const getSlideCount = () => el.querySelectorAll('.swiper-slide').length;

    const mount = () => {
      if (typeof window.Swiper !== 'function') return;
      const mq = window.matchMedia(DESKTOP_MQ);
      if (!mq.matches) {
        if (swiper) {
          try {
            swiper.destroy(true, true);
          } catch (_) {}
          swiper = null;
        }
        section.classList.remove('is-editions-swiper-inited');
        if (navWrap instanceof HTMLElement) {
          navWrap.classList.remove('is-visible');
          navWrap.setAttribute('aria-hidden', 'true');
        }
        return;
      }

      const c = getSlideCount();
      if (c < 1) return;

      if (swiper) {
        try {
          swiper.destroy(true, true);
        } catch (_) {}
        swiper = null;
      }

      const showNav = c > 4;
      if (navWrap instanceof HTMLElement) {
        if (showNav) {
          navWrap.classList.add('is-visible');
          navWrap.setAttribute('aria-hidden', 'false');
        } else {
          navWrap.classList.remove('is-visible');
          navWrap.setAttribute('aria-hidden', 'true');
        }
      }

      const opts = {
        spaceBetween: 20,
        watchOverflow: true,
        watchSlidesProgress: true,
        slidesPerView: c < 4 ? 'auto' : 4,
        centeredSlides: c < 4,
        centeredSlidesBounds: c < 4,
        on: {
          init: function () {
            scheduleNexodeAirTemplateScrollTriggerRefresh();
          },
          slideChange: function () {
            scheduleNexodeAirTemplateScrollTriggerRefresh();
          }
        }
      };

      if (showNav && prev && next) {
        opts.navigation = { prevEl: prev, nextEl: next };
      }

      try {
        swiper = new window.Swiper(el, opts);
        section.classList.add('is-editions-swiper-inited');
        scheduleNexodeAirTemplateScrollTriggerRefresh();
      } catch (err) {
        try {
          console.error('Nexode Air editions Swiper', err);
        } catch (_) {}
      }
    };

    const onResize = () => {
      clearTimeout(resizeT);
      resizeT = window.setTimeout(mount, 220);
    };

    if (typeof window.Swiper === 'function') {
      mount();
    } else {
      swiperWait = window.setInterval(() => {
        if (typeof window.Swiper === 'function') {
          clearInterval(swiperWait);
          swiperWait = 0;
          mount();
        }
      }, 80);
      const waitMax = window.setTimeout(() => {
        if (swiperWait) {
          clearInterval(swiperWait);
          swiperWait = 0;
        }
      }, 10000);
      cleanups.push(() => clearTimeout(waitMax));
    }

    window.addEventListener('resize', onResize);
    cleanups.push(() => {
      clearTimeout(resizeT);
      if (swiperWait) clearInterval(swiperWait);
      window.removeEventListener('resize', onResize);
      if (swiper) {
        try {
          swiper.destroy(true, true);
        } catch (_) {}
        swiper = null;
      }
      section.classList.remove('is-editions-swiper-inited');
    });
  }

  /**
   * 手机端：>4 个产品时在主题中配置的「View More」展开第 5 张及之后（与 CSS 截断配合）。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initNexodeAirEditionsMoViewMore(root, ctx) {
    const { cleanups } = ctx;
    const section = root.querySelector(
      '.ug-nexode-air-template__editions[data-na-editions-has-many]'
    );
    const btn = section && section.querySelector('[data-na-editions-mo-view-more]');
    if (!section || !(btn instanceof HTMLButtonElement)) return;

    const onClick = () => {
      section.classList.add('is-na-mo-expanded');
      btn.setAttribute('aria-expanded', 'true');
      try {
        scheduleNexodeAirTemplateScrollTriggerRefresh();
      } catch (_) {}
    };

    btn.addEventListener('click', onClick);
    cleanups.push(() => {
      try {
        btn.removeEventListener('click', onClick);
      } catch (_) {}
    });
  }

  /**
   * 系列卡：变体切换（图/价/背景 preset）+ 无渠道时同步 Buy 链接。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; reduced?: boolean }} ctx
   */
  function initNexodeAirEditionsCards(root, ctx) {
    const { cleanups, reduced: prefersReduced } = ctx;
    const cards = root.querySelectorAll('[data-na-editions-card]');
    if (!cards.length) return;

    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const script = card.querySelector('script[data-na-editions-data]');
      const rows = naEditionsParseRows(script);
      if (!Array.isArray(rows) || !rows.length) return;

      const imgEl = card.querySelector('[data-na-editions-img]');
      const priceEl = card.querySelector('[data-na-editions-price]');
      const compareEl = card.querySelector('[data-na-editions-compare]');
      const surfaceEl = card.querySelector('[data-na-editions-surface]');
      const ctaZone = card.querySelector('[data-na-editions-cta]');
      const buyLink = ctaZone?.querySelector(
        'a.ug-nexode-air-template__charge-promo-buy[data-na-buy-product-link], a.ug-nexode-air-template__charge-promo-buy'
      );

      const PRESETS = ['peach', 'gray', 'paper', 'mist'];
      const VARIANT_FADE_MS = 280;
      let variantFadeGen = 0;

      const findRow = (variantId) => {
        const s = String(variantId).trim();
        const n = /^\d+$/.test(s) ? parseInt(s, 10) : NaN;
        const hit = rows.find((r) => {
          if (r == null) return false;
          if (String(r.id).trim() === s) return true;
          if (!Number.isNaN(n) && Number(r.id) === n) return true;
          return false;
        });
        return hit || rows[0];
      };

      /**
       * 变体行应用到 DOM（主图/价/链接/preset/CTA 等），不含淡入淡出时序；首屏初始化与无障碍「减弱动效」时直接调用。
       *
       * @param {object} row
       * @param {HTMLElement | null} swatchEl
       */
      const applyRowCore = (row, swatchEl) => {
        if (!row) return;
        if (imgEl instanceof HTMLImageElement && row.image) {
          imgEl.src = row.image;
          imgEl.removeAttribute('srcset');
        }
        if (imgEl instanceof HTMLImageElement) {
          imgEl.style.removeProperty('opacity');
        }
        if (priceEl) {
          const pt = row && row.price_text;
          if (pt != null && String(pt).trim() !== '') {
            priceEl.textContent = String(pt).trim();
          } else {
            priceEl.textContent = naEditionsFormatMoney(row.price_cents);
          }
        }
        if (compareEl) {
          const ct = row && row.compare_text;
          if (ct != null && String(ct).trim() !== '' && String(ct) !== 'null') {
            compareEl.textContent = String(ct).trim();
            compareEl.removeAttribute('hidden');
          } else {
            const cc = row.compare_cents;
            const pc = row.price_cents;
            if (cc != null && pc != null && Number(cc) > Number(pc)) {
              compareEl.textContent = naEditionsFormatMoney(cc);
              compareEl.removeAttribute('hidden');
            } else {
              compareEl.textContent = '';
              compareEl.setAttribute('hidden', '');
            }
          }
        }
        PRESETS.forEach((p) => card.classList.remove(`is-preset-${p}`));
        card.classList.remove('is-swatch-bg');
        if (surfaceEl instanceof HTMLElement) {
          surfaceEl.style.removeProperty('--edc-variant-swatch');
        }
        const presetFromRow = row.preset && String(row.preset).trim();
        const presetFromSwatch =
          swatchEl instanceof HTMLElement ? swatchEl.getAttribute('data-preset') : '';
        const preset = presetFromRow || (presetFromSwatch ? String(presetFromSwatch).trim() : '');
        if (preset && PRESETS.includes(preset)) {
          card.classList.add(`is-preset-${preset}`);
        }
        if (buyLink instanceof HTMLAnchorElement && row.url) {
          buyLink.href = String(row.url);
        }
      };

      const applyRow = (row, swatchEl) => {
        if (!row) return;
        const useFade = swatchEl instanceof HTMLElement && !prefersReduced;
        if (!useFade) {
          card.classList.remove('is-editions-variant-fade');
          applyRowCore(row, swatchEl);
          return;
        }
        const myGen = ++variantFadeGen;
        card.classList.add('is-editions-variant-fade');
        window.setTimeout(() => {
          if (myGen !== variantFadeGen) return;
          const endFade = () => {
            if (myGen !== variantFadeGen) return;
            requestAnimationFrame(() => {
              if (myGen !== variantFadeGen) return;
              card.classList.remove('is-editions-variant-fade');
            });
          };
          if (imgEl instanceof HTMLImageElement && row.image) {
            const onDone = () => {
              if (imgEl) {
                imgEl.removeEventListener('load', onDone);
                imgEl.removeEventListener('error', onDone);
              }
              endFade();
            };
            imgEl.addEventListener('load', onDone, { once: true });
            imgEl.addEventListener('error', onDone, { once: true });
            applyRowCore(row, swatchEl);
            if (imgEl.complete) onDone();
          } else {
            applyRowCore(row, swatchEl);
            endFade();
          }
        }, VARIANT_FADE_MS);
      };

      const onCardPointer = (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        const sw = t.closest('[data-na-editions-swatch]');
        if (!sw || !card.contains(sw)) return;
        const id = sw.getAttribute('data-variant-id');
        if (id == null || String(id).trim() === '') return;
        card.querySelectorAll('[data-na-editions-swatch]').forEach((x) => {
          const active = x === sw;
          x.classList.toggle('is-selected', active);
          x.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        applyRow(findRow(id), sw);
      };
      /* capture: true，优先于主题其它 click 监听 */
      card.addEventListener('click', onCardPointer, true);
      cleanups.push(() => card.removeEventListener('click', onCardPointer, true));

      applyRowCore(rows[0], null);
    });
  }

  function initNexodeAirBuyDropdowns(root, ctx) {
    const { cleanups } = ctx;
    const wraps = root.querySelectorAll('[data-na-buy-dropdown]');
    if (!wraps.length) return;

    /* 旅行场景轮播克隆卡：不参与下拉绑定，避免与主卡重复 */
    const isTravelSceneClone = (el) =>
      !!(el instanceof Element && el.closest('[data-na-travel-scene-clone]'));

    const mqBuyDdDesk =
      typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 1024px)') : null;
    const mqBuyDdMobile =
      typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 1023.98px)') : null;

    const prefersHoverOpen =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      (!mqBuyDdDesk || mqBuyDdDesk.matches);

    /* hover 离开按钮与面板之间有绝对定位空隙，会误触 mouseleave；延迟略短减轻「拖沓」感 */
    const hoverLeaveTimers = new WeakMap();
    const HOVER_LEAVE_MS = 160;

    /* 手机弹层挂 body 后需记住归属卡，close 时挂回（否则祖先 transform/overflow 会裁切 fixed） */
    const buyDdModalPortalOwner = new WeakMap();

    const restoreBuyDdModalToWrap = (modalEl) => {
      if (!(modalEl instanceof HTMLElement)) return;
      const owner = buyDdModalPortalOwner.get(modalEl);
      if (owner instanceof HTMLElement && modalEl.parentNode === document.body) {
        owner.appendChild(modalEl);
      }
    };

    const setBuyDdModalDomOpen = (modalEl, open) => {
      if (!(modalEl instanceof HTMLElement)) return;
      if (open) modalEl.setAttribute('data-na-buy-modal-open', '1');
      else modalEl.removeAttribute('data-na-buy-modal-open');
    };

    const mountBuyDdModalToBody = (modalEl, ownerWrap) => {
      if (!(modalEl instanceof HTMLElement) || !(ownerWrap instanceof HTMLElement)) return;
      if (!(mqBuyDdMobile && mqBuyDdMobile.matches)) return;
      buyDdModalPortalOwner.set(modalEl, ownerWrap);
      if (modalEl.parentNode !== document.body) {
        document.body.appendChild(modalEl);
      }
    };

    const clearBuyDdScrollLock = () => {
      try {
        document.documentElement.classList.remove('na-buy-dd-scroll-lock');
        document.body.classList.remove('na-buy-dd-scroll-lock');
      } catch (_) {}
    };

    const applyBuyDdScrollLockIfMobile = () => {
      if (mqBuyDdMobile && mqBuyDdMobile.matches) {
        try {
          document.documentElement.classList.add('na-buy-dd-scroll-lock');
          document.body.classList.add('na-buy-dd-scroll-lock');
        } catch (_) {}
      }
    };

    const clearDdHoverLeaveTimer = (wEl) => {
      if (!(wEl instanceof HTMLElement)) return;
      const tid = hoverLeaveTimers.get(wEl);
      if (tid != null) window.clearTimeout(tid);
      hoverLeaveTimers.delete(wEl);
    };

    /** @type {{ wrap: HTMLElement; toggle: HTMLElement; panel: HTMLElement; modal: HTMLElement | null; modalDismiss: HTMLElement | null; modalClose: HTMLElement | null }[]} */
    const buyDdInstances = [];
    wraps.forEach((wrap) => {
      if (!(wrap instanceof HTMLElement)) return;
      if (isTravelSceneClone(wrap)) return;
      const toggle = wrap.querySelector('[data-na-buy-dropdown-toggle]');
      const panel = wrap.querySelector('[data-na-buy-dropdown-panel]');
      const modal = wrap.querySelector('[data-na-buy-dropdown-modal]');
      if (!(toggle instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
      if (toggle.hasAttribute('disabled')) return;
      const modalDismiss =
        modal instanceof HTMLElement ? modal.querySelector('[data-na-buy-dropdown-modal-dismiss]') : null;
      const modalClose =
        modal instanceof HTMLElement ? modal.querySelector('[data-na-buy-dropdown-modal-close]') : null;
      buyDdInstances.push({
        wrap,
        toggle,
        panel,
        modal: modal instanceof HTMLElement ? modal : null,
        modalDismiss: modalDismiss instanceof HTMLElement ? modalDismiss : null,
        modalClose: modalClose instanceof HTMLElement ? modalClose : null,
      });
    });

    const closeAllInRoot = () => {
      buyDdInstances.forEach(({ wrap, toggle, panel, modal }) => {
        clearDdHoverLeaveTimer(wrap);
        if (modal) {
          setBuyDdModalDomOpen(modal, false);
          restoreBuyDdModalToWrap(modal);
        }
        wrap.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        if (panel.dataset.naDdMounted !== '1') {
          panel.hidden = true;
        }
      });
      clearBuyDdScrollLock();
    };

    const onBuyDdCrossToDesk = () => {
      if (mqBuyDdDesk && mqBuyDdDesk.matches) {
        closeAllInRoot();
      }
    };
    if (mqBuyDdDesk) {
      mqBuyDdDesk.addEventListener('change', onBuyDdCrossToDesk);
      cleanups.push(() => mqBuyDdDesk.removeEventListener('change', onBuyDdCrossToDesk));
    }

    buyDdInstances.forEach(({ wrap, toggle, panel, modal, modalDismiss, modalClose }) => {
      /* 首屏即关：移除原生 hidden，改由 is-open + CSS 过渡控制（克隆卡仍保留 hidden） */
      wrap.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      panel.removeAttribute('hidden');
      panel.dataset.naDdMounted = '1';

      const close = () => {
        clearDdHoverLeaveTimer(wrap);
        if (modal) {
          setBuyDdModalDomOpen(modal, false);
          restoreBuyDdModalToWrap(modal);
        }
        wrap.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        clearBuyDdScrollLock();
      };

      const open = () => {
        clearDdHoverLeaveTimer(wrap);
        closeAllInRoot();
        panel.dataset.naDdInteractive = '1';
        wrap.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        applyBuyDdScrollLockIfMobile();
        if (modal) {
          setBuyDdModalDomOpen(modal, true);
          mountBuyDdModalToBody(modal, wrap);
        }
      };

      if (prefersHoverOpen) {
        const onEnter = () => {
          clearDdHoverLeaveTimer(wrap);
          open();
        };
        const onLeave = () => {
          clearDdHoverLeaveTimer(wrap);
          const tid = window.setTimeout(() => {
            hoverLeaveTimers.delete(wrap);
            close();
          }, HOVER_LEAVE_MS);
          hoverLeaveTimers.set(wrap, tid);
        };
        wrap.addEventListener('mouseenter', onEnter);
        wrap.addEventListener('mouseleave', onLeave);
        cleanups.push(() => wrap.removeEventListener('mouseenter', onEnter));
        cleanups.push(() => wrap.removeEventListener('mouseleave', onLeave));

        /* 键盘：Tab 进入购买区时展开，离开整块区域后收起 */
        const onFocusIn = () => {
          open();
        };
        const onFocusOut = (e) => {
          const next = e.relatedTarget;
          if (next instanceof Node && wrap.contains(next)) return;
          if (next instanceof Node && modal && modal.contains(next)) return;
          close();
        };
        wrap.addEventListener('focusin', onFocusIn);
        wrap.addEventListener('focusout', onFocusOut);
        cleanups.push(() => wrap.removeEventListener('focusin', onFocusIn));
        cleanups.push(() => wrap.removeEventListener('focusout', onFocusOut));

        /* 精指针：以 hover 为主，click 作兜底（触控板/未触发 mouseenter 时仍可展开） */
        const onToggleClickHover = (e) => {
          e.stopPropagation();
          if (wrap.classList.contains('is-open')) close();
          else open();
        };
        toggle.addEventListener('click', onToggleClickHover);
        cleanups.push(() => toggle.removeEventListener('click', onToggleClickHover));
      } else {
        const onToggleClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (wrap.classList.contains('is-open')) close();
          else open();
        };
        toggle.addEventListener('click', onToggleClick);
        cleanups.push(() => toggle.removeEventListener('click', onToggleClick));
      }

      const onModalDismiss = (e) => {
        e.preventDefault();
        e.stopPropagation();
        close();
      };
      if (modalDismiss) {
        modalDismiss.addEventListener('click', onModalDismiss);
        cleanups.push(() => modalDismiss.removeEventListener('click', onModalDismiss));
      }
      if (modalClose) {
        modalClose.addEventListener('click', onModalDismiss);
        cleanups.push(() => modalClose.removeEventListener('click', onModalDismiss));
      }
    });

    const onDocPointerDown = (e) => {
      const el = e.target instanceof Element ? e.target : null;
      if (!el || el.closest('[data-na-buy-dropdown]') || el.closest('[data-na-buy-dropdown-modal]')) return;
      closeAllInRoot();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    cleanups.push(() => document.removeEventListener('pointerdown', onDocPointerDown, true));

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeAllInRoot();
    };
    document.addEventListener('keydown', onKeyDown);
    cleanups.push(() => document.removeEventListener('keydown', onKeyDown));

    cleanups.push(() => {
      buyDdInstances.forEach(({ wrap, modal }) => {
        clearDdHoverLeaveTimer(wrap);
        if (modal) {
          setBuyDdModalDomOpen(modal, false);
          restoreBuyDdModalToWrap(modal);
        }
      });
      clearBuyDdScrollLock();
    });
  }

  /**
   * 65W Charge Promo：进度条、跑马灯与购买区交互。
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; reduced: boolean }} ctx
   */
  function initNexodeAirChargePromo(root, ctx) {
    const { cleanups, reduced } = ctx;
    const chargeSections = root.querySelectorAll('section.ug-nexode-air-template__charge-promo[data-na-charge-promo]');
    if (!chargeSections.length) return;

    /** @param {HTMLElement} section */
    const activateChargePromoSection = (section) => {
      section.classList.add('is-charge-promo-meters-active');
      section.querySelectorAll('[data-na-charge-promo-devices-track]').forEach((track) => {
        if (track instanceof HTMLElement && track.classList.contains('is-marquee')) {
          track.classList.add('is-marquee-active');
        }
      });
    };

    chargeSections.forEach((section) => {
      if (!(section instanceof HTMLElement)) return;

      if (reduced) {
        activateChargePromoSection(section);
      } else {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((en) => {
              if (!en.isIntersecting) return;
              activateChargePromoSection(section);
              io.disconnect();
            });
          },
          { root: null, threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
        );
        io.observe(section);
        cleanups.push(() => {
          try {
            io.disconnect();
          } catch (_) {}
        });
      }
    });
  }

  /**
   * Travel Kit：桌面手风琴/双卡 pin 与手机双组切换。
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; registered: unknown[]; reduced: boolean }} ctx
   */
  function initNexodeTravelKit(root, ctx) {
    const { cleanups, reduced: travelKitReduced } = ctx;

    const TRAVEL_KIT_PIN_MQ = '(min-width: 1024px)';

    /**
     * 双卡桌面：吸顶叠放，第二屏通过 clip-path 覆盖第一屏。
     * @param {HTMLElement} section
     * @param {{ cleanups: Array<() => void>; reduced: boolean }} ctx2
     */
    function initNexodeTravelKitHorizontalPin(section, ctx2) {
      const { cleanups: cu, reduced: r } = ctx2;
      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;
      if (!gsap || !ScrollTrigger || r) {
        return;
      }
      const pinStage = section.querySelector('[data-na-travel-kit-pin-inner]');
      const track = section.querySelector('[data-na-travel-kit-h-track]');
      if (!(pinStage instanceof HTMLElement) || !(track instanceof HTMLElement)) {
        return;
      }

      const cells = track.querySelectorAll('.ug-nexode-air-template__travel-kit-h-cell');
      if (cells.length < 2) {
        return;
      }
      const secondCell = cells[1];
      if (!(secondCell instanceof HTMLElement)) {
        return;
      }

      const mm = ScrollTrigger.matchMedia({
        [TRAVEL_KIT_PIN_MQ]: () => {
          // 第二屏初始全裁剪，滚动时逐步揭示
          gsap.set(secondCell, {
            clipPath: 'inset(0% 0% 0% 100%)',
            WebkitClipPath: 'inset(0% 0% 0% 100%)',
          });
          // 与视口高度一致，约一屏完成揭示
          const scrollSpan = () =>
            typeof window !== 'undefined'
              ? Math.max(1, window.innerHeight || pinStage.offsetHeight)
              : Math.max(1, pinStage.offsetHeight);
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: pinStage,
              start: 'top top',
              end: () => '+=' + scrollSpan(),
              pin: true,
              scrub: true,
              invalidateOnRefresh: true,
            },
          });
          tl.to(secondCell, {
            clipPath: 'inset(0% 0% 0% 0%)',
            WebkitClipPath: 'inset(0% 0% 0% 0%)',
            ease: 'none',
          });

          return () => {
            tl.kill();
          };
        },
      });

      cu.push(() => {
        try {
          mm.revert();
        } catch (_) {}
      });
    }

    /**
     * 手机双组：顶层 Tab 切换 `data-na-travel-kit-mo-group-pane`
     * @param {HTMLElement} section
     */
    function bindTravelKitMoGroupTabs(section) {
      const moRoot = section.querySelector('[data-na-travel-kit-mo-root]');
      if (!(moRoot instanceof HTMLElement)) {
        return;
      }
      const tabs = Array.from(moRoot.querySelectorAll('[data-na-travel-kit-group-tab]'));
      const panes = Array.from(moRoot.querySelectorAll('[data-na-travel-kit-mo-group-pane]'));
      if (!tabs.length || !panes.length) {
        return;
      }

      const tabBar = moRoot.querySelector('.ug-nexode-air-template__travel-kit-mo-group-tabs');

      const activate = (/** @type {number} */ idx) => {
        tabs.forEach((btn, i) => {
          if (!(btn instanceof HTMLElement)) {
            return;
          }
          const on = i === idx;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panes.forEach((pane, i) => {
          if (!(pane instanceof HTMLElement)) {
            return;
          }
          const on = i === idx;
          pane.classList.toggle('is-active', on);
          if (on) {
            pane.removeAttribute('hidden');
          } else {
            pane.setAttribute('hidden', '');
          }
        });
        // 横向滚动条：切换后将当前 Tab 滚入可视区（居中优先）
        const activeBtn = tabs[idx];
        if (tabBar instanceof HTMLElement && activeBtn instanceof HTMLElement) {
          activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      };

      tabs.forEach((btn, idx) => {
        const onClick = () => activate(idx);
        btn.addEventListener('click', onClick);
        cleanups.push(() => btn.removeEventListener('click', onClick));
      });
    }

    /**
     * 桌面右侧：与 UGREEN 专题一致，多图叠层 + `is-active` + CSS opacity 过渡
     * @param {HTMLElement | null} heroStack
     * @param {string} panel
     */
    const switchTravelHeroLayers = (heroStack, panel) => {
      if (!(heroStack instanceof HTMLElement)) {
        return;
      }
      if (!heroStack.hasAttribute('data-na-travel-hero-stack')) {
        return;
      }
      const p = String(panel);
      if (String(heroStack.dataset.naTravelPanel || '1') === p) {
        return;
      }
      const layers = heroStack.querySelectorAll('[data-na-travel-hero-layer]');
      if (!layers.length) {
        return;
      }
      let hit = false;
      layers.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }
        const lp = node.getAttribute('data-na-travel-hero-layer') || '';
        const on = lp === p;
        if (on) {
          hit = true;
        }
        node.classList.toggle('is-active', on);
      });
      if (hit) {
        heroStack.dataset.naTravelPanel = p;
      }
    };

    /**
     * 手风琴：左侧瞬时收起上一项；展开子块错开渐显（与 UGREEN 时长/曲线对齐）；桌面右侧叠层切图
     * @param {HTMLElement} accRoot
     * @param {HTMLElement | null} heroStack
     * @param {{ reduced: boolean }} accOpts
     */
    const bindAccordion = (accRoot, heroStack, accOpts) => {
      const items = Array.from(accRoot.querySelectorAll('[data-na-travel-acc-item]'));
      if (!items.length) {
        return;
      }
      const gsap = window.gsap;
      const useMotion = Boolean(gsap) && !accOpts.reduced;
      // 手机旅行手风琴（与桌面 data-na-travel-acc 区分）：展开内层错开动画略放慢、曲线更柔和
      const isMoTravelAccordion = accRoot.hasAttribute('data-na-travel-acc-mo');

      const setItemVisualState = (/** @type {HTMLElement} */ item, /** @type {boolean} */ open) => {
        item.classList.toggle('is-open', open);
        const trigger = item.querySelector('[data-na-travel-acc-trigger]');
        if (trigger instanceof HTMLElement) {
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        const titleEl = item.querySelector('.ug-nexode-air-template__travel-kit-acc-title');
        if (titleEl instanceof HTMLElement) {
          titleEl.classList.toggle('ug-nexode-air-template__travel-kit-acc-title--collapsed', !open);
        }
        const panel = item.querySelector('.ug-nexode-air-template__travel-kit-acc-panel');
        if (panel instanceof HTMLElement) {
          panel.setAttribute('aria-hidden', open ? 'false' : 'true');
        }
      };

      /**
       * 切换条目时：当前项收起不做过渡（瞬时收起），仅下一项展开保留 CSS 动画
       * @param {HTMLElement} item
       */
      const snapCloseTravelAccItem = (item) => {
        const closingPanel = item.querySelector('.ug-nexode-air-template__travel-kit-acc-panel');
        if (closingPanel instanceof HTMLElement) {
          closingPanel.style.transition = 'none';
        }
        setItemVisualState(item, false);
        if (closingPanel instanceof HTMLElement) {
          void closingPanel.offsetHeight;
          closingPanel.style.removeProperty('transition');
        }
      };

      /**
       * 展开面板内子元素错开渐显（与 UGREEN：`acc-panel-inner` 为 scope 根）
       * @param {HTMLElement} panelEl
       */
      const staggerPanelContent = (panelEl) => {
        if (!useMotion || !gsap) {
          return;
        }
        const innerEl =
          panelEl.querySelector(':scope > .ug-nexode-air-template__travel-kit-acc-panel-inner') ||
          panelEl;
        const parts = innerEl.querySelectorAll(
          ':scope > .ug-nexode-air-template__travel-kit-desc--acc, :scope > .ug-nexode-air-template__travel-kit-desc--plain, :scope > p, :scope > .ug-nexode-air-template__travel-kit-chips, :scope > .ug-nexode-air-template__travel-kit-mo-hero, :scope > [data-na-travel-charge-meters], :scope > .ug-nexode-air-template__travel-kit-meter-note'
        );
        if (!parts.length) {
          return;
        }
        gsap.fromTo(
          parts,
          { opacity: 0, y: 8 },
          {
            opacity: 1,
            y: 0,
            duration: isMoTravelAccordion ? 0.54 : 0.48,
            stagger: isMoTravelAccordion ? 0.12 : 0.1,
            ease: isMoTravelAccordion ? 'power2.inOut' : 'power3.out',
            delay: isMoTravelAccordion ? 0.1 : 0.08,
            clearProps: 'opacity,transform',
          }
        );
      };

      const openPanelIndex = (/** @type {number} */ targetIdx) => {
        items.forEach((node, idx) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }
          const wasOpen = node.classList.contains('is-open');
          const willOpen = idx === targetIdx;
          setItemVisualState(node, willOpen);
          if (willOpen && !wasOpen) {
            const panelEl = node.querySelector('.ug-nexode-air-template__travel-kit-acc-panel');
            if (panelEl instanceof HTMLElement) {
              staggerPanelContent(panelEl);
            }
          }
          if (idx === targetIdx && heroStack) {
            const panel = node.getAttribute('data-panel') || '1';
            switchTravelHeroLayers(heroStack, panel);
          }
        });
      };

      const onTriggerClick = (ev) => {
        const t = ev.currentTarget;
        if (!(t instanceof HTMLElement)) {
          return;
        }
        const item = t.closest('[data-na-travel-acc-item]');
        if (!(item instanceof HTMLElement)) {
          return;
        }
        const idx = items.indexOf(item);
        if (idx < 0) {
          return;
        }
        if (item.classList.contains('is-open')) {
          return;
        }

        const openIdx = items.findIndex((el) => el instanceof HTMLElement && el.classList.contains('is-open'));
        if (openIdx >= 0 && openIdx !== idx) {
          const prevItem = items[openIdx];
          if (prevItem instanceof HTMLElement) {
            snapCloseTravelAccItem(prevItem);
          }
        }
        openPanelIndex(idx);
      };

      items.forEach((node) => {
        const triggers = node.querySelectorAll('[data-na-travel-acc-trigger]');
        triggers.forEach((tr) => {
          tr.addEventListener('click', onTriggerClick);
          cleanups.push(() => tr.removeEventListener('click', onTriggerClick));
        });
      });
    };

    root.querySelectorAll('[data-na-travel-kit]').forEach((section) => {
      if (!(section instanceof HTMLElement)) {
        return;
      }

      section.querySelectorAll('[data-na-travel-kit-slide]').forEach((slide) => {
        if (!(slide instanceof HTMLElement)) {
          return;
        }

        const desktop = slide.querySelector('[data-na-travel-desktop]');
        const heroStack = slide.querySelector('[data-na-travel-hero-stack]');
        if (desktop instanceof HTMLElement) {
          const acc = desktop.querySelector('[data-na-travel-acc]');
          if (acc instanceof HTMLElement) {
            if (heroStack instanceof HTMLElement) {
              heroStack.dataset.naTravelPanel = '1';
            }
            bindAccordion(
              acc,
              heroStack instanceof HTMLElement ? heroStack : null,
              { reduced: travelKitReduced }
            );
          }
        }

        const mobile = slide.querySelector('[data-na-travel-mobile]');
        if (mobile instanceof HTMLElement) {
          mobile.querySelectorAll('[data-na-travel-acc-mo]').forEach((accMo) => {
            if (accMo instanceof HTMLElement) {
              bindAccordion(accMo, null, { reduced: travelKitReduced });
            }
          });

          const segBtns = mobile.querySelectorAll('[data-na-travel-seg-btn]');
          const panes = mobile.querySelectorAll('[data-na-travel-mo-pane]');
          if (segBtns.length && panes.length) {
            const activateSeg = (/** @type {string} */ seg) => {
              segBtns.forEach((btn) => {
                if (!(btn instanceof HTMLElement)) {
                  return;
                }
                const isMatch = btn.getAttribute('data-travel-seg') === seg;
                btn.classList.toggle('is-active', isMatch);
                btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
              });
              panes.forEach((pane) => {
                if (!(pane instanceof HTMLElement)) {
                  return;
                }
                const match = pane.getAttribute('data-travel-mo-pane') === seg;
                pane.classList.toggle('is-active', match);
                if (match) {
                  pane.removeAttribute('hidden');
                } else {
                  pane.setAttribute('hidden', '');
                }
              });
            };

            const onSegClick = (ev) => {
              const b = ev.currentTarget;
              if (!(b instanceof HTMLElement)) {
                return;
              }
              const seg = b.getAttribute('data-travel-seg');
              if (!seg) {
                return;
              }
              activateSeg(seg);
            };

            segBtns.forEach((btn) => {
              btn.addEventListener('click', onSegClick);
              cleanups.push(() => btn.removeEventListener('click', onSegClick));
            });
          }
        }
      });

      bindTravelKitMoGroupTabs(section);
      section.querySelectorAll('[data-na-travel-kit-mo-root] [data-na-travel-acc-mo]').forEach((accMo) => {
        if (accMo instanceof HTMLElement) {
          bindAccordion(accMo, null, { reduced: travelKitReduced });
        }
      });

      initNexodeTravelKitHorizontalPin(section, ctx);
    });
  }

  /**
   * 旅行场景轮播：与 UGREEN工作 一致（首尾克隆 + transitionend 对齐真实索引）；lion 保留左右箭头。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; reduced: boolean }} ctx
   */
  function initNexodeTravelScene(root, ctx) {
    const { cleanups, reduced } = ctx;
    root.querySelectorAll('[data-na-travel-scene]').forEach((section) => {
      if (!(section instanceof HTMLElement)) {
        return;
      }
      const viewport = section.querySelector('[data-na-travel-scene-viewport]');
      const track = section.querySelector('[data-na-travel-scene-track]');
      const navPrev = section.querySelector('[data-na-travel-scene-prev]');
      const navNext = section.querySelector('[data-na-travel-scene-next]');
      const tabs = Array.from(section.querySelectorAll('[data-na-travel-scene-tab]'));
      const cards = Array.from(section.querySelectorAll('[data-na-travel-scene-card]')).filter((card) => card instanceof HTMLElement);
      if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || !tabs.length || !cards.length) {
        return;
      }

      const realCount = cards.length;
      const loopEnabled = realCount > 1;
      let autoTimer = 0;

      if (loopEnabled) {
        const firstClone = cards[0].cloneNode(true);
        const lastClone = cards[realCount - 1].cloneNode(true);
        const resetClonedBuyDropdowns = (cloneRoot) => {
          cloneRoot.querySelectorAll('[data-na-buy-dropdown]').forEach((w) => {
            if (!(w instanceof HTMLElement)) return;
            w.classList.remove('is-open');
            const t = w.querySelector('[data-na-buy-dropdown-toggle]');
            const p = w.querySelector('[data-na-buy-dropdown-panel]');
            if (t instanceof HTMLElement) {
              t.setAttribute('aria-expanded', 'false');
              t.removeAttribute('aria-controls');
            }
            if (p instanceof HTMLElement) {
              p.removeAttribute('id');
              p.hidden = true;
              /* 深拷贝可能带上主卡已 init 的 data-*，清掉避免克隆节点残留下拉态 */
              delete p.dataset.naDdMounted;
              delete p.dataset.naDdInteractive;
            }
          });
        };

        if (firstClone instanceof HTMLElement) {
          firstClone.removeAttribute('data-scene-index');
          firstClone.classList.remove('is-active');
          firstClone.setAttribute('aria-hidden', 'true');
          firstClone.setAttribute('data-na-travel-scene-clone', 'first');
          resetClonedBuyDropdowns(firstClone);
          track.appendChild(firstClone);
        }
        if (lastClone instanceof HTMLElement) {
          lastClone.removeAttribute('data-scene-index');
          lastClone.classList.remove('is-active');
          lastClone.setAttribute('aria-hidden', 'true');
          lastClone.setAttribute('data-na-travel-scene-clone', 'last');
          resetClonedBuyDropdowns(lastClone);
          track.insertBefore(lastClone, track.firstChild);
        }
      }

      const slides = Array.from(track.children).filter((slide) => slide instanceof HTMLElement);
      let currentSlide = loopEnabled ? 1 : 0;
      let activeIndex = 0;
      let isDragging = false;
      let dragPointerId = null;
      let dragStartX = 0;
      let dragBaseX = 0;
      let dragDeltaX = 0;

      const getRealIndexBySlide = (slideIndex) => {
        if (!loopEnabled) {
          return Math.max(0, Math.min(slideIndex, realCount - 1));
        }
        if (slideIndex <= 0) {
          return realCount - 1;
        }
        if (slideIndex >= realCount + 1) {
          return 0;
        }
        return slideIndex - 1;
      };

      const getTargetTranslateX = (slideIndex) => {
        const activeCard = slides[slideIndex];
        if (!(activeCard instanceof HTMLElement) || viewport.clientWidth <= 0) {
          return 0;
        }
        const rawTarget = activeCard.offsetLeft + activeCard.offsetWidth / 2 - viewport.clientWidth / 2;
        return -rawTarget;
      };

      const getCurrentTranslateX = () => {
        const tf = window.getComputedStyle(track).transform;
        if (!tf || tf === 'none') {
          return getTargetTranslateX(currentSlide);
        }
        const m2d = tf.match(/^matrix\((.+)\)$/);
        if (m2d && m2d[1]) {
          const parts = m2d[1].split(',').map((s) => Number.parseFloat(s.trim()));
          return Number.isFinite(parts[4]) ? parts[4] : getTargetTranslateX(currentSlide);
        }
        const m3d = tf.match(/^matrix3d\((.+)\)$/);
        if (m3d && m3d[1]) {
          const parts = m3d[1].split(',').map((s) => Number.parseFloat(s.trim()));
          return Number.isFinite(parts[12]) ? parts[12] : getTargetTranslateX(currentSlide);
        }
        return getTargetTranslateX(currentSlide);
      };

      const updateUiBySlide = () => {
        activeIndex = getRealIndexBySlide(currentSlide);
        tabs.forEach((btn, i) => {
          if (!(btn instanceof HTMLElement)) {
            return;
          }
          const on = i === activeIndex;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        cards.forEach((card, i) => {
          card.classList.toggle('is-active', i === activeIndex);
        });
      };

      const updateTrackTranslate = (immediate, slideIndex = currentSlide) => {
        const targetX = getTargetTranslateX(slideIndex);
        if (immediate) {
          track.style.transition = 'none';
          track.style.transform = `translate3d(${targetX}px, 0, 0)`;
          window.requestAnimationFrame(() => {
            track.style.removeProperty('transition');
          });
          return;
        }
        track.style.transform = `translate3d(${targetX}px, 0, 0)`;
      };

      const activateByRealIndex = (idx, immediate) => {
        const safeIdx = Math.max(0, Math.min(idx, realCount - 1));
        currentSlide = loopEnabled ? safeIdx + 1 : safeIdx;
        updateUiBySlide();
        updateTrackTranslate(immediate, currentSlide);
      };

      const shiftSlide = (delta) => {
        if (!loopEnabled) {
          const safe = Math.max(0, Math.min(currentSlide + delta, realCount - 1));
          currentSlide = safe;
        } else {
          currentSlide += delta;
        }
        updateUiBySlide();
        updateTrackTranslate(false, currentSlide);
      };

      const clearAutoPlay = () => {
        if (autoTimer) {
          window.clearInterval(autoTimer);
          autoTimer = 0;
        }
      };

      const startAutoPlay = () => {
        clearAutoPlay();
        if (realCount <= 1 || reduced) {
          return;
        }
        autoTimer = window.setInterval(() => {
          shiftSlide(1);
        }, 3800);
      };

      const restartAutoPlay = () => {
        startAutoPlay();
      };

      tabs.forEach((btn, idx) => {
        const onClick = () => {
          activateByRealIndex(idx, false);
          restartAutoPlay();
        };
        btn.addEventListener('click', onClick);
        cleanups.push(() => btn.removeEventListener('click', onClick));
      });

      const bindNavBtn = (btn, delta) => {
        if (!(btn instanceof HTMLElement) || realCount <= 1) {
          return;
        }
        const onNavClick = (ev) => {
          if (ev instanceof MouseEvent && ev.button !== 0) {
            return;
          }
          shiftSlide(delta);
          restartAutoPlay();
        };
        btn.addEventListener('click', onNavClick);
        cleanups.push(() => btn.removeEventListener('click', onNavClick));
      };
      bindNavBtn(navPrev, -1);
      bindNavBtn(navNext, 1);

      const onTrackTransitionEnd = (ev) => {
        if (!(ev instanceof TransitionEvent) || ev.propertyName !== 'transform' || !loopEnabled) {
          return;
        }
        if (currentSlide === realCount + 1) {
          currentSlide = 1;
          updateUiBySlide();
          updateTrackTranslate(true, currentSlide);
        } else if (currentSlide === 0) {
          currentSlide = realCount;
          updateUiBySlide();
          updateTrackTranslate(true, currentSlide);
        }
      };
      track.addEventListener('transitionend', onTrackTransitionEnd);
      cleanups.push(() => track.removeEventListener('transitionend', onTrackTransitionEnd));

      const onResize = () => updateTrackTranslate(true);
      window.addEventListener('resize', onResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onResize));

      const onPointerDown = (ev) => {
        if (!(ev instanceof PointerEvent) || realCount <= 1) {
          return;
        }
        const targetEl = ev.target instanceof Element ? ev.target : null;
        if (
          targetEl
          && targetEl.closest(
            '.ug-nexode-air-template__charge-promo-buy, a, button'
          )
        ) {
          return;
        }
        if (ev.pointerType === 'mouse' && ev.button !== 0) {
          return;
        }
        isDragging = true;
        dragPointerId = ev.pointerId;
        dragStartX = ev.clientX;
        dragDeltaX = 0;
        dragBaseX = getCurrentTranslateX();
        clearAutoPlay();
        track.style.transition = 'none';
        section.classList.add('is-travel-scene-dragging');
        try {
          viewport.setPointerCapture(ev.pointerId);
        } catch (_) {}
        ev.preventDefault();
      };

      const onPointerMove = (ev) => {
        if (!(ev instanceof PointerEvent) || !isDragging || dragPointerId !== ev.pointerId) {
          return;
        }
        dragDeltaX = ev.clientX - dragStartX;
        track.style.transform = `translate3d(${dragBaseX + dragDeltaX}px, 0, 0)`;
      };

      const finishDrag = (ev) => {
        if (!(ev instanceof PointerEvent) || !isDragging || dragPointerId !== ev.pointerId) {
          return;
        }
        isDragging = false;
        dragPointerId = null;
        section.classList.remove('is-travel-scene-dragging');
        try {
          viewport.releasePointerCapture(ev.pointerId);
        } catch (_) {}
        track.style.removeProperty('transition');
        const threshold = Math.max(40, viewport.clientWidth * 0.08);
        if (Math.abs(dragDeltaX) >= threshold) {
          shiftSlide(dragDeltaX < 0 ? 1 : -1);
        } else {
          updateTrackTranslate(false, currentSlide);
        }
        dragDeltaX = 0;
        restartAutoPlay();
      };

      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', finishDrag);
      viewport.addEventListener('pointercancel', finishDrag);
      cleanups.push(() => viewport.removeEventListener('pointerdown', onPointerDown));
      cleanups.push(() => viewport.removeEventListener('pointermove', onPointerMove));
      cleanups.push(() => viewport.removeEventListener('pointerup', finishDrag));
      cleanups.push(() => viewport.removeEventListener('pointercancel', finishDrag));

      const onMouseEnter = () => clearAutoPlay();
      const onMouseLeave = () => startAutoPlay();
      section.addEventListener('mouseenter', onMouseEnter);
      section.addEventListener('mouseleave', onMouseLeave);
      cleanups.push(() => section.removeEventListener('mouseenter', onMouseEnter));
      cleanups.push(() => section.removeEventListener('mouseleave', onMouseLeave));

      const onVisibilityChange = () => {
        if (document.hidden) {
          clearAutoPlay();
        } else {
          startAutoPlay();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      cleanups.push(() => document.removeEventListener('visibilitychange', onVisibilityChange));

      activateByRealIndex(0, true);
      startAutoPlay();
      cleanups.push(() => clearAutoPlay());
    });
  }

  /**
   * KOL 视频列表：横向卡片滑动（按钮 + 拖拽），与专题断点统一。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; reduced: boolean }} ctx
   */
  function initNexodeAirKolVideos(root, ctx) {
    const { cleanups, reduced } = ctx;
    root.querySelectorAll('[data-na-kol]').forEach((section) => {
      if (!(section instanceof HTMLElement)) {
        return;
      }
      const viewport = section.querySelector('[data-na-kol-viewport]');
      const track = section.querySelector('[data-na-kol-track]');
      const prev = section.querySelector('[data-na-kol-prev]');
      const next = section.querySelector('[data-na-kol-next]');
      const cards = Array.from(section.querySelectorAll('[data-na-kol-card]')).filter((card) => card instanceof HTMLElement);
      if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || cards.length === 0) {
        return;
      }

      let index = 0;
      let cardStep = 0;
      let visibleCount = 1;
      let maxIndex = 0;
      let isDragging = false;
      let dragPointerId = null;
      let dragStartX = 0;
      let dragDeltaX = 0;
      let dragBaseX = 0;

      const normalizeYoutubeEmbed = (rawUrl) => {
        if (typeof rawUrl !== 'string') {
          return '';
        }
        const url = rawUrl.trim();
        if (!url) {
          return '';
        }
        try {
          const parsed = new URL(url, window.location.origin);
          let id = '';
          if (parsed.hostname.includes('youtu.be')) {
            id = parsed.pathname.replace('/', '');
          } else if (parsed.hostname.includes('youtube.com')) {
            if (parsed.pathname.startsWith('/shorts/')) {
              id = parsed.pathname.split('/')[2] || '';
            } else if (parsed.pathname.startsWith('/embed/')) {
              id = parsed.pathname.split('/')[2] || '';
            } else {
              id = parsed.searchParams.get('v') || '';
            }
          }
          if (!id) {
            return '';
          }
          return `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
        } catch (_) {
          return '';
        }
      };

      const stopOtherVideos = (exceptCard = null) => {
        cards.forEach((card) => {
          if (!(card instanceof HTMLElement) || card === exceptCard) {
            return;
          }
          card.classList.remove('is-playing');
          const wrap = card.querySelector('[data-na-kol-video-wrap]');
          if (wrap instanceof HTMLElement) {
            wrap.innerHTML = '';
          }
        });
      };

      const playCardVideo = (card) => {
        if (!(card instanceof HTMLElement)) {
          return;
        }
        if (card.classList.contains('is-outside')) {
          return;
        }
        const youtubeUrl = card.getAttribute('data-na-kol-youtube-url') || '';
        const embedUrl = normalizeYoutubeEmbed(youtubeUrl);
        if (!embedUrl) {
          return;
        }
        const wrap = card.querySelector('[data-na-kol-video-wrap]');
        if (!(wrap instanceof HTMLElement)) {
          return;
        }
        stopOtherVideos(card);
        if (!card.classList.contains('is-playing')) {
          wrap.innerHTML = '';
          const iframe = document.createElement('iframe');
          iframe.src = embedUrl;
          iframe.title = 'YouTube video player';
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
          iframe.allowFullscreen = true;
          wrap.appendChild(iframe);
          card.classList.add('is-playing');
        }
      };

      const parseTranslateX = () => {
        const tf = window.getComputedStyle(track).transform;
        if (!tf || tf === 'none') {
          return -(index * cardStep);
        }
        const m2d = tf.match(/^matrix\((.+)\)$/);
        if (m2d && m2d[1]) {
          const parts = m2d[1].split(',').map((s) => Number.parseFloat(s.trim()));
          return Number.isFinite(parts[4]) ? parts[4] : -(index * cardStep);
        }
        const m3d = tf.match(/^matrix3d\((.+)\)$/);
        if (m3d && m3d[1]) {
          const parts = m3d[1].split(',').map((s) => Number.parseFloat(s.trim()));
          return Number.isFinite(parts[12]) ? parts[12] : -(index * cardStep);
        }
        return -(index * cardStep);
      };

      const updateNav = () => {
        if (prev instanceof HTMLElement) {
          prev.classList.toggle('is-disabled', index <= 0);
        }
        if (next instanceof HTMLElement) {
          next.classList.toggle('is-disabled', index >= maxIndex);
        }
      };

      const updateOutsideState = () => {
        const viewportRect = viewport.getBoundingClientRect();
        cards.forEach((card) => {
          if (!(card instanceof HTMLElement)) {
            return;
          }
          const rect = card.getBoundingClientRect();
          const edgeTolerance = 1;
          const isOutside = rect.left < viewportRect.left - edgeTolerance || rect.right > viewportRect.right + edgeTolerance;
          card.classList.toggle('is-outside', isOutside);
        });
      };

      const moveTo = (targetIndex, immediate = false) => {
        const safeIndex = Math.max(0, Math.min(targetIndex, maxIndex));
        index = safeIndex;
        stopOtherVideos();
        const x = -(safeIndex * cardStep);
        if (immediate || reduced) {
          track.style.transition = 'none';
          track.style.transform = `translate3d(${x}px, 0, 0)`;
          window.requestAnimationFrame(() => {
            track.style.removeProperty('transition');
          });
        } else {
          track.style.transform = `translate3d(${x}px, 0, 0)`;
        }
        updateNav();
        window.requestAnimationFrame(updateOutsideState);
      };

      const recalc = () => {
        const firstCard = cards[0];
        if (!(firstCard instanceof HTMLElement)) {
          return;
        }
        const computed = window.getComputedStyle(track);
        const gap = Number.parseFloat(computed.columnGap || computed.gap || '0') || 0;
        cardStep = firstCard.offsetWidth + gap;
        visibleCount = Math.max(1, Math.floor((viewport.clientWidth + gap) / Math.max(1, cardStep)));
        maxIndex = Math.max(0, cards.length - visibleCount);
        moveTo(Math.min(index, maxIndex), true);
      };

      const onPrev = () => moveTo(index - 1);
      const onNext = () => moveTo(index + 1);
      if (prev instanceof HTMLElement) {
        prev.addEventListener('click', onPrev);
        cleanups.push(() => prev.removeEventListener('click', onPrev));
      }
      if (next instanceof HTMLElement) {
        next.addEventListener('click', onNext);
        cleanups.push(() => next.removeEventListener('click', onNext));
      }

      cards.forEach((card) => {
        const onPlay = (ev) => {
          if (ev instanceof Event) {
            ev.preventDefault();
            ev.stopPropagation();
          }
          playCardVideo(card);
        };
        const playTriggers = Array.from(card.querySelectorAll('[data-na-kol-play]')).filter((el) => el instanceof HTMLElement);
        playTriggers.forEach((trigger) => {
          trigger.addEventListener('click', onPlay);
          cleanups.push(() => trigger.removeEventListener('click', onPlay));
        });
      });

      const onPointerDown = (ev) => {
        if (!(ev instanceof PointerEvent)) {
          return;
        }
        const targetEl = ev.target instanceof Element ? ev.target : null;
        if (targetEl && targetEl.closest('[data-na-kol-play], [data-na-kol-video-wrap], iframe')) {
          return;
        }
        if (ev.pointerType === 'mouse' && ev.button !== 0) {
          return;
        }
        isDragging = true;
        dragPointerId = ev.pointerId;
        dragStartX = ev.clientX;
        dragDeltaX = 0;
        dragBaseX = parseTranslateX();
        track.style.transition = 'none';
        viewport.classList.add('is-dragging');
        try {
          viewport.setPointerCapture(ev.pointerId);
        } catch (_) {}
        ev.preventDefault();
      };

      const onPointerMove = (ev) => {
        if (!(ev instanceof PointerEvent) || !isDragging || dragPointerId !== ev.pointerId) {
          return;
        }
        dragDeltaX = ev.clientX - dragStartX;
        track.style.transform = `translate3d(${dragBaseX + dragDeltaX}px, 0, 0)`;
        updateOutsideState();
      };

      const onPointerEnd = (ev) => {
        if (!(ev instanceof PointerEvent) || !isDragging || dragPointerId !== ev.pointerId) {
          return;
        }
        isDragging = false;
        dragPointerId = null;
        viewport.classList.remove('is-dragging');
        try {
          viewport.releasePointerCapture(ev.pointerId);
        } catch (_) {}
        track.style.removeProperty('transition');
        const threshold = Math.max(40, viewport.clientWidth * 0.08);
        if (Math.abs(dragDeltaX) >= threshold) {
          moveTo(index + (dragDeltaX < 0 ? 1 : -1));
        } else {
          moveTo(index);
        }
        dragDeltaX = 0;
      };

      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', onPointerEnd);
      viewport.addEventListener('pointercancel', onPointerEnd);
      cleanups.push(() => viewport.removeEventListener('pointerdown', onPointerDown));
      cleanups.push(() => viewport.removeEventListener('pointermove', onPointerMove));
      cleanups.push(() => viewport.removeEventListener('pointerup', onPointerEnd));
      cleanups.push(() => viewport.removeEventListener('pointercancel', onPointerEnd));

      const onResize = () => recalc();
      window.addEventListener('resize', onResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onResize));

      const onTrackTransitionEnd = (ev) => {
        if (!(ev instanceof TransitionEvent) || ev.propertyName !== 'transform') {
          return;
        }
        updateOutsideState();
      };
      track.addEventListener('transitionend', onTrackTransitionEnd);
      cleanups.push(() => track.removeEventListener('transitionend', onTrackTransitionEnd));

      recalc();
    });
  }

  /**
   * 与专题内容区一致：以 1280px 水线换算初始缩放（满宽视口时约 1280/vw），居中放大至满屏。
   * @returns {number}
   */
  function getPackSceneStartScale() {
    const w = Math.max(320, window.innerWidth || document.documentElement.clientWidth || 1280);
    const mo = typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;
    const gutter = mo ? 32 : 80;
    const contentW = Math.min(1280, Math.max(280, w - gutter));
    return Math.min(1, contentW / w);
  }

  /**
   * Pack 场景：`[data-na-pack-scene-scroll]` 内 sticky 满屏，滚动 scrub 顺序为
   * 桌面两段 scrub：① `top bottom`→`top top` 渐显 + 缩放至铺满；② `top top`→`bottom bottom` 先蒙版再文案（overlay → inner）。
   * 手机端：不跑 GSAP 时间轴；蒙版先随滚动渐显，满显后再渐显 inner（`--na-pack-mo-overlay-opacity` / `--na-pack-mo-inner-opacity`）。
   * 无 GSAP 或 `prefers-reduced-motion` 时加 `is-na-pack-static` 直接终态（手机蒙版满显）。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>; registered: unknown[]; reduced: boolean }} ctx
   */
  function initNexodeAirPackScene(root, ctx) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const { cleanups, reduced } = ctx;

    root.querySelectorAll('[data-na-pack-scene-scroll]').forEach((scrollEl) => {
      if (!(scrollEl instanceof HTMLElement)) {
              return;
            }
      const card = scrollEl.querySelector('[data-na-pack-scene-card]');
      const inner = scrollEl.querySelector('[data-na-pack-scene-inner]');
      const overlay = scrollEl.querySelector('[data-na-pack-scene-overlay]');
      if (!(card instanceof HTMLElement)) {
        return;
      }

      const isPackMobile =
        typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;

      if (!gsap || !ScrollTrigger || reduced) {
        scrollEl.classList.add('is-na-pack-static');
        if (isPackMobile) {
          scrollEl.style.setProperty('--na-pack-mo-overlay-opacity', '1');
          scrollEl.style.setProperty('--na-pack-mo-inner-opacity', '1');
        }
        return;
      }

      if (isPackMobile) {
        scrollEl.classList.add('is-na-pack-static');

        const updatePackMoOverlay = () => {
          const vh = window.innerHeight || 1;
          const rect = scrollEl.getBoundingClientRect();
          const h = Math.max(1, rect.height);
          /* 当 scroll 块顶边位于该 top 时，块几何中心落在视口水平中线 */
          const topStart = vh * 0.5 - h * 0.5;
          let o = 0;
          if (rect.top <= 0) {
            o = 1;
          } else if (topStart > 0) {
            if (rect.top < topStart) {
              o = (topStart - rect.top) / topStart;
            }
          } else if (rect.top > 0) {
            /* 块高 ≥ 视口：用顶边从视口底向 0 线性近似 */
            o = 1 - rect.top / vh;
          }
          o = Math.max(0, Math.min(1, o));
          /* 前半段蒙版渐显，后半段文案渐显 */
          const overlayO = Math.min(1, o * 2);
          const innerO = Math.max(0, Math.min(1, (o - 0.5) * 2));
          scrollEl.style.setProperty('--na-pack-mo-overlay-opacity', String(overlayO));
          scrollEl.style.setProperty('--na-pack-mo-inner-opacity', String(innerO));
        };

        let moOverlayRaf = 0;
        const scheduleMoOverlay = () => {
          if (moOverlayRaf) return;
          moOverlayRaf = window.requestAnimationFrame(() => {
            moOverlayRaf = 0;
            updatePackMoOverlay();
          });
        };

        updatePackMoOverlay();
        window.addEventListener('scroll', scheduleMoOverlay, { passive: true });
        window.addEventListener('resize', scheduleMoOverlay, { passive: true });
        cleanups.push(() => {
          window.removeEventListener('scroll', scheduleMoOverlay);
          window.removeEventListener('resize', scheduleMoOverlay);
          if (moOverlayRaf) {
            try {
              window.cancelAnimationFrame(moOverlayRaf);
            } catch (_) {}
            moOverlayRaf = 0;
          }
          try {
            scrollEl.style.removeProperty('--na-pack-mo-overlay-opacity');
            scrollEl.style.removeProperty('--na-pack-mo-inner-opacity');
          } catch (_) {}
        });
        return;
      }

      const startScale = getPackSceneStartScale();
      /** 第一段（至 top top）内：前 30% 渐显，后 70% 缩放至铺满 */
      const P1_FADE = 0.4;
      const P1_SCALE = 0.6;
      const T_TEXT = 0.55;
      const T_MASK = 0.45;
      const scrubPack = 0.35;

      gsap.set(scrollEl, { autoAlpha: 0 });
      gsap.set(card, {
        scale: startScale,
        transformOrigin: '50% 50%'
      });
      if (inner instanceof HTMLElement) {
        gsap.set(inner, { autoAlpha: 0, y: 28 });
      }
      if (overlay instanceof HTMLElement) {
        gsap.set(overlay, { autoAlpha: 0 });
      }

      /* 顶边进视口 → 顶边贴视口顶：此段结束时卡片 scale 必为 1（铺满 sticky 区） */
      const tlEnter = gsap.timeline({
        scrollTrigger: {
          trigger: scrollEl,
          start: 'top bottom',
          end: 'top top',
          scrub: scrubPack,
          invalidateOnRefresh: true
        }
      });

      tlEnter.fromTo(
        scrollEl,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: P1_FADE, ease: 'none' },
        0
      );
      tlEnter.fromTo(
        card,
        { scale: () => getPackSceneStartScale() },
        { scale: 1, duration: P1_SCALE, ease: 'none' },
        P1_FADE
      );

      let tlCopy = null;
      if (inner instanceof HTMLElement || overlay instanceof HTMLElement) {
        tlCopy = gsap.timeline({
          scrollTrigger: {
            trigger: scrollEl,
            start: 'top top',
            end: 'bottom bottom',
            scrub: scrubPack,
            invalidateOnRefresh: true
          }
        });

        let copyPos = 0;
        if (overlay instanceof HTMLElement) {
          tlCopy.fromTo(
            overlay,
            { autoAlpha: 0 },
            { autoAlpha: 1, duration: T_MASK, ease: 'power2.out' },
            copyPos
          );
          copyPos += T_MASK;
        }
        if (inner instanceof HTMLElement) {
          tlCopy.fromTo(
            inner,
            { autoAlpha: 0, y: 28 },
            { autoAlpha: 1, y: 0, duration: T_TEXT, ease: 'power2.out' },
            copyPos
          );
        }
      }

      cleanups.push(() => {
        try {
          tlEnter.kill();
        } catch (_) {}
        try {
          if (tlCopy) {
            tlCopy.kill();
          }
        } catch (_) {}
      });

      const syncPackTimelineIfPast = (tl) => {
        try {
          const st = tl.scrollTrigger;
          if (!st) {
            return;
          }
          st.refresh();
          const sp = typeof st.progress === 'number' ? st.progress : 0;
          const tp = typeof tl.totalProgress === 'function' ? tl.totalProgress() : 0;
          if (tp > 0.02) {
            return;
          }
          if (sp >= 0.999) {
            tl.progress(1);
          } else if (sp > 0.04) {
            tl.progress(sp);
          }
        } catch (_) {}
      };

      const syncPackIfAlreadyPast = () => {
        syncPackTimelineIfPast(tlEnter);
        if (tlCopy) {
          syncPackTimelineIfPast(tlCopy);
        }
      };
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(syncPackIfAlreadyPast);
      });
    });
  }

  /**
   * KV 导航：fixed 不占文档流；初始隐藏，滚过 KV 底部后滑入显示；滚回 KV 上方再收起。
   * 手机端：品牌 + 下箭头展开下拉（Highlights / Sign Up），右侧 Buy Now 保持 Figma 规格。
   *
   * @param {HTMLElement} root
   * @param {{ cleanups: Array<() => void>, reduced: boolean }} ctx
   */
  function initNexodeAirKvNav(root, ctx) {
    const nav = root.querySelector('[data-na-kv-nav]');
    if (!(nav instanceof HTMLElement)) {
      return;
    }

    const kvEl = root.querySelector('[data-na-kv]') || root.querySelector('.ug-nexode-air-template__kv');
    const buyBtn = nav.querySelector('[data-na-kv-nav-buy]');
    const buyTarget = root.querySelector('#na-editions') || root.querySelector('.ug-nexode-air-template__editions');
    const dropdownToggle = nav.querySelector('[data-na-kv-nav-dropdown-toggle]');
    const dropdownPanel = nav.querySelector('[data-na-kv-nav-dropdown]');
    /** 手机下拉关闭（无下拉时为 no-op） */
    let closeMobileKvMenu = () => {};

    let kvRevealThreshold = 0;
    let lastSy = window.scrollY || window.pageYOffset || 0;

    const measureKvRevealThreshold = () => {
      if (kvEl instanceof HTMLElement) {
        const rect = kvEl.getBoundingClientRect();
        const sy0 = window.scrollY || window.pageYOffset || 0;
        kvRevealThreshold = Math.max(0, Math.round(rect.bottom + sy0));
        return;
      }
      kvRevealThreshold = 0;
    };

    const closeDropdownForStickyHide = () => {
      closeMobileKvMenu();
      /* 已挂载过渡态的面板勿再设 hidden，否则与 Buy 下拉一致的 grid 动画被打断 */
      if (dropdownPanel instanceof HTMLElement && dropdownPanel.dataset.naKvDdMounted !== '1') {
        dropdownPanel.hidden = true;
      }
    };

    const finishLeave = () => {
      nav.removeEventListener('animationend', onLeaveAnimEnd);
      nav.classList.remove('is-kv-sticky', 'is-kv-sticky-leaving');
      nav.classList.add('is-kv-nav-before-kv');
      nav.setAttribute('aria-hidden', 'true');
      closeDropdownForStickyHide();
      lastSy = window.scrollY || window.pageYOffset || 0;
      window.requestAnimationFrame(() => {
        measureKvRevealThreshold();
      });
    };

    const onLeaveAnimEnd = (ev) => {
      if (!(ev instanceof AnimationEvent)) {
        return;
      }
      if (ev.target !== nav) {
        return;
      }
      if (ev.animationName !== 'ug-na-kv-nav-slide-out') {
        return;
      }
      finishLeave();
    };

    const startLeave = () => {
      if (!nav.classList.contains('is-kv-sticky') || nav.classList.contains('is-kv-sticky-leaving')) {
        return;
      }
      closeDropdownForStickyHide();
      if (ctx.reduced) {
        finishLeave();
        return;
      }
      nav.classList.add('is-kv-sticky-leaving');
      nav.addEventListener('animationend', onLeaveAnimEnd);
    };

    measureKvRevealThreshold();

    let ticking = false;
    const updateStickyState = () => {
      ticking = false;
      const sy = window.scrollY || window.pageYOffset || 0;
      const wantVisible = sy >= kvRevealThreshold;

      if (nav.classList.contains('is-kv-sticky-leaving')) {
        if (wantVisible) {
          nav.removeEventListener('animationend', onLeaveAnimEnd);
          nav.classList.remove('is-kv-sticky-leaving');
          nav.classList.add('is-kv-sticky');
        }
        lastSy = sy;
        return;
      }

      if (wantVisible) {
        if (nav.classList.contains('is-kv-nav-before-kv')) {
          nav.classList.remove('is-kv-nav-before-kv');
          nav.removeAttribute('aria-hidden');
          if (!ctx.reduced) {
            void nav.offsetHeight;
          }
          nav.classList.remove('is-kv-sticky-leaving');
          nav.classList.add('is-kv-sticky');
        } else if (!nav.classList.contains('is-kv-sticky')) {
          nav.classList.add('is-kv-sticky');
        }
      } else if (nav.classList.contains('is-kv-sticky')) {
        startLeave();
      } else if (!nav.classList.contains('is-kv-nav-before-kv')) {
        nav.classList.add('is-kv-nav-before-kv');
        nav.setAttribute('aria-hidden', 'true');
      }

      lastSy = sy;
    };

    const requestStickyUpdate = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(updateStickyState);
    };

    const onResize = () => {
      measureKvRevealThreshold();
      requestStickyUpdate();
    };

    requestStickyUpdate();
    window.addEventListener('scroll', requestStickyUpdate, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    ctx.cleanups.push(() => window.removeEventListener('scroll', requestStickyUpdate));
    ctx.cleanups.push(() => window.removeEventListener('resize', onResize));
    ctx.cleanups.push(() => {
      nav.removeEventListener('animationend', onLeaveAnimEnd);
      nav.classList.remove('is-kv-sticky', 'is-kv-sticky-leaving', 'is-kv-nav-before-kv');
      nav.removeAttribute('aria-hidden');
    });

    const jumpTo = (targetEl) => {
      if (!(targetEl instanceof HTMLElement)) {
        return;
      }
      const sy = window.scrollY || window.pageYOffset || 0;
      const navH = Math.max(0, Math.round(nav.getBoundingClientRect().height));
      const targetTop = targetEl.getBoundingClientRect().top + sy;
      const nextTop = Math.max(
        0,
        targetTop - navH - NA_KV_NAV_ANCHOR_GAP_BELOW_NAV_PX + NA_KV_NAV_ANCHOR_EXTRA_SCROLL_PX
      );
      window.scrollTo({
        top: nextTop,
        behavior: ctx.reduced ? 'auto' : 'smooth'
      });
    };

    const getHashFromHref = (href) => {
      if (typeof href !== 'string' || href.length === 0) {
        return '';
      }
      if (href.startsWith('#')) {
        return href;
      }
      try {
        const url = new URL(href, window.location.href);
        return url.hash || '';
      } catch (_) {
        return '';
      }
    };

    const getTargetByHash = (hash) => {
      if (typeof hash !== 'string' || !hash.startsWith('#')) {
        return null;
      }
      const target = root.querySelector(hash) || document.querySelector(hash);
      return target instanceof HTMLElement ? target : null;
    };

    const anchorEls = Array.from(nav.querySelectorAll('[data-na-kv-nav-anchor]'));
    const onAnchorClick = (ev) => {
      const link = ev.currentTarget;
      if (!(link instanceof HTMLAnchorElement)) {
        return;
      }
      const hash = getHashFromHref(link.getAttribute('href') || '');
      const target = getTargetByHash(hash);
      if (!target) {
        return;
      }
      ev.preventDefault();
      if (dropdownPanel instanceof HTMLElement && dropdownPanel.contains(link)) {
        closeMobileKvMenu();
      }
      jumpTo(target);
    };
    anchorEls.forEach((el) => {
      el.addEventListener('click', onAnchorClick);
      ctx.cleanups.push(() => el.removeEventListener('click', onAnchorClick));
    });

    const onBuyNowClick = (ev) => {
      if (!(buyTarget instanceof HTMLElement)) {
        return;
      }
      ev.preventDefault();
      jumpTo(buyTarget);
    };
    if (buyBtn instanceof HTMLElement) {
      buyBtn.addEventListener('click', onBuyNowClick);
      ctx.cleanups.push(() => buyBtn.removeEventListener('click', onBuyNowClick));
    }

    if (dropdownToggle instanceof HTMLElement && dropdownPanel instanceof HTMLElement) {
      const mqMobile = window.matchMedia('(max-width: 1023.98px)');

      /* 与 Buy 下拉一致：移除原生 hidden，用 is-kv-dropdown-open + CSS grid 0fr→1fr 做过渡 */
      dropdownPanel.removeAttribute('hidden');
      dropdownPanel.dataset.naKvDdMounted = '1';

      const closeDropdown = () => {
        nav.classList.remove('is-kv-dropdown-open');
        dropdownToggle.setAttribute('aria-expanded', 'false');
      };

      const openDropdown = () => {
        nav.classList.add('is-kv-dropdown-open');
        dropdownToggle.setAttribute('aria-expanded', 'true');
      };

      const toggleDropdown = () => {
        if (nav.classList.contains('is-kv-dropdown-open')) {
          closeDropdown();
          return;
        }
        openDropdown();
      };

      const onToggleClick = (ev) => {
        /* 以「触发按钮是否可见」为准，避免 matchMedia 与 CSS 断点偶发不一致导致点击被忽略 */
        try {
          if (getComputedStyle(dropdownToggle).display === 'none') {
            return;
          }
        } catch (_) {
          if (!mqMobile.matches) {
            return;
          }
        }
        ev.preventDefault();
        ev.stopPropagation();
        toggleDropdown();
      };

      const onDocumentClick = (ev) => {
        if (!(ev.target instanceof Node)) {
          return;
        }
        const t = ev.target;
        if (dropdownToggle instanceof HTMLElement && (t === dropdownToggle || dropdownToggle.contains(t))) {
          return;
        }
        if (dropdownPanel instanceof HTMLElement && (t === dropdownPanel || dropdownPanel.contains(t))) {
          return;
        }
        if (!nav.contains(t)) {
          /* 延后一帧关闭，避免与按钮 click 同一次事件路径上其它监听冲突 */
          window.requestAnimationFrame(() => {
            closeDropdown();
          });
        }
      };

      const onKeyDown = (ev) => {
        if (ev.key === 'Escape') {
          closeDropdown();
        }
      };

      const onViewportChange = () => {
        if (!mqMobile.matches) {
          closeDropdown();
        }
      };

      closeDropdown();
      dropdownToggle.addEventListener('click', onToggleClick);
      document.addEventListener('click', onDocumentClick);
      document.addEventListener('keydown', onKeyDown);
      window.addEventListener('resize', onViewportChange, { passive: true });
      ctx.cleanups.push(() => dropdownToggle.removeEventListener('click', onToggleClick));
      ctx.cleanups.push(() => document.removeEventListener('click', onDocumentClick));
      ctx.cleanups.push(() => document.removeEventListener('keydown', onKeyDown));
      ctx.cleanups.push(() => window.removeEventListener('resize', onViewportChange));
      ctx.cleanups.push(() => closeDropdown());

      closeMobileKvMenu = closeDropdown;
    }
  }

  /**
   * 专题交互入口：注册并初始化各区块 ScrollTrigger，返回统一 cleanup。
   * @param {HTMLElement} root
   * @returns {() => void}
   */
  function initNexodeAirTemplateFeatureSections(root) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const cleanups = [];
    const registered = [];
    const reduced = naPrefersReducedMotion();

    if (gsap && ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      try {
        const de = document.documentElement;
        if (de && de.dataset.ugNexodeAirTemplateScrollSnapFix !== '1') {
          de.dataset.ugNexodeAirTemplateScrollSnapFix = '1';
          de.style.scrollBehavior = 'auto';
        }
      } catch (_) {}
    }

    const ctx = { cleanups, registered, reduced };

    initNexodeAirKvNav(root, ctx);
    initWhenNexode65wHighlight(root, ctx);
    initWhenNexode65wAir65(root, ctx);
    /* Pack 场景放在 Air65 之后注册，避免 pin 占位影响触发时机 */
    initNexodeAirPackScene(root, ctx);
    initNexodeAirWhyAir(root, ctx);
    initNexodeAirTemplateColorCompat(root, ctx);
    initNexodeAirEditionsSwiper(root, ctx);
    initNexodeAirEditionsMoViewMore(root, ctx);
    initNexodeAirEditionsCards(root, ctx);
    initNexodeAirBuyDropdowns(root, ctx);
    initNexodeAirTvcModal(root, ctx);
    initNexodeAirTvcInlineVideo(root, ctx);
    initNexodeAirChargePromo(root, ctx);
    initNexodeTravelKit(root, ctx);
    initNexodeTravelScene(root, ctx);
    initNexodeAirKolVideos(root, ctx);

    scheduleNexodeAirTemplateScrollTriggerRefresh();

    return function cleanup() {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch (_) {}
      });
      naKillRegisteredTriggers(registered);
    };
  }

  if (typeof window !== 'undefined') {
    window.UgNexodeAirTemplateReveal = {
      defaults: REVEAL_DEFAULTS,
      init: initNexodeAirTemplateScrollReveal,
      refresh: refreshNexodeAirTemplateScrollReveal
    };
  }

  /**
   * 订阅邮箱格式校验。
   * @param {string} email
   * @returns {boolean}
   */
  function validateEmailFormat(email) {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      String(email).toLowerCase()
    );
  }

  /** Web Component 生命周期：挂载初始化，卸载清理。 */
  class NexodeAirTemplateSection extends HTMLElement {
    constructor() {
      super();
      this._onVideoResize = null;
      this._kvResizeRaf = 0;
      this._naRevealCleanup = null;
      this._naFeatureCleanup = null;
    }

    connectedCallback() {
      if (this.dataset.naInited === '1') {
        return;
      }
      this.dataset.naInited = '1';

      /* 先注册 pin / 手风琴等 ScrollTrigger，再跑渐显，避免版心后段模块的 reveal 起点被 pin 占位算提前 */
      this._naFeatureCleanup = initNexodeAirTemplateFeatureSections(this);
      this._naRevealCleanup = initNexodeAirTemplateScrollReveal(this);

      this.initKvVideo();
      this.initEmailBlocks();
    }

    disconnectedCallback() {
      if (this._kvResizeRaf) {
        window.cancelAnimationFrame(this._kvResizeRaf);
        this._kvResizeRaf = 0;
      }
      if (this._onVideoResize) {
        window.removeEventListener('resize', this._onVideoResize);
        this._onVideoResize = null;
      }
      this.dataset.naVideoResizeBound = '0';

      if (typeof this._naRevealCleanup === 'function') {
        this._naRevealCleanup();
        this._naRevealCleanup = null;
      }

      if (typeof this._naFeatureCleanup === 'function') {
        this._naFeatureCleanup();
        this._naFeatureCleanup = null;
      }

      /* 主题编辑器或同节点再次挂载时需允许重新初始化 */
      delete this.dataset.naInited;
      this.querySelectorAll('.ug-nexode-air-template__email[data-na-email]').forEach((el) => {
        if (el instanceof HTMLElement) {
          delete el.dataset.naEmailInited;
        }
      });
    }

    /** KV 双视频：与 UGREEN air 一致（resize 节流 + `naPlayBufferedInlineVideo`） */
    initKvVideo() {
      const posterImg = this.querySelector('.ug-nexode-air-template__kv-poster');
      let lastIsMobile = null;

      const sync = () => {
        const isMobile = window.matchMedia(MOBILE_MQ).matches;
        if (lastIsMobile !== null && lastIsMobile === isMobile) {
          return;
        }
        lastIsMobile = isMobile;

        const active = isMobile
          ? this.querySelector('.ug-nexode-air-template__kv-video--mo')
          : this.querySelector('.ug-nexode-air-template__kv-video--pc');
        const inactive = isMobile
          ? this.querySelector('.ug-nexode-air-template__kv-video--pc')
          : this.querySelector('.ug-nexode-air-template__kv-video--mo');

        if (inactive instanceof HTMLVideoElement) {
          try {
            inactive.pause();
            inactive.preload = 'none';
          } catch (_) {}
          inactive.classList.remove('is-visible');
        }
        if (!(active instanceof HTMLVideoElement)) {
          return;
        }

        try {
          active.preload = 'auto';
        } catch (_) {}

        const srcEl = active.querySelector('source');
        const src = srcEl && srcEl.getAttribute('src');
        if (!src || src.trim() === '') {
          return;
        }

        const tryPlay = () => {
          active.classList.add('is-visible');
          if (posterImg instanceof HTMLElement) {
            posterImg.classList.add('is-hidden');
          }
          naPlayBufferedInlineVideo(active);
        };

        if (active.readyState >= 3) {
          tryPlay();
        } else {
          active.addEventListener('canplay', tryPlay, { once: true });
          active.addEventListener('loadeddata', tryPlay, { once: true });
          try {
            active.load();
          } catch (_) {}
        }
      };

      const scheduleSync = () => {
        if (this._kvResizeRaf) {
          window.cancelAnimationFrame(this._kvResizeRaf);
        }
        this._kvResizeRaf = window.requestAnimationFrame(() => {
          this._kvResizeRaf = 0;
          sync();
        });
      };

      scheduleSync();

      if (this.dataset.naVideoResizeBound === '1') {
        return;
      }
      this.dataset.naVideoResizeBound = '1';
      this._onVideoResize = scheduleSync;
      window.addEventListener('resize', this._onVideoResize, { passive: true });
    }

    /** 初始化留邮区块。 */
    initEmailBlocks() {
      const blocks = this.querySelectorAll('.ug-nexode-air-template__email[data-na-email]');
      blocks.forEach((block) => {
        this.bindEmailBlock(block);
      });
    }

    /**
     * 绑定留邮提交逻辑。
     * @param {Element} block
     */
    bindEmailBlock(block) {
      if (!(block instanceof HTMLElement)) {
        return;
      }
      if (block.dataset.naEmailInited === '1') {
        return;
      }
      block.dataset.naEmailInited = '1';

      const submitBtn = block.querySelector('.ug-nexode-air-template__email-submit');
      const input = block.querySelector('.ug-nexode-air-template__email-input');
      const privacyCheckbox = block.querySelector('.ug-nexode-air-template__email-checkbox');
      const errorMsg = block.querySelector('.ug-nexode-air-template__email-error');
      const successMsg = block.querySelector('.ug-nexode-air-template__email-message');

      const formId = block.getAttribute('data-form-id') || '';
      const successText = block.getAttribute('data-success-text') || 'Thanks for subscribing!';
      const failText = block.getAttribute('data-fail-text') || 'Subscription failed, please try again later.';
      const privacyError = block.getAttribute('data-privacy-error') || 'Please agree to the privacy policy.';
      const emailError = block.getAttribute('data-email-error') || 'Please enter a valid email address.';

      if (!(submitBtn instanceof HTMLButtonElement) || !(input instanceof HTMLInputElement)) {
        return;
      }

      const showError = (msg) => {
        if (errorMsg instanceof HTMLElement) {
          errorMsg.textContent = msg;
          errorMsg.style.display = 'block';
        }
        if (successMsg instanceof HTMLElement) {
          successMsg.style.display = 'none';
        }
      };

      const showSuccess = (msg) => {
        if (successMsg instanceof HTMLElement) {
          successMsg.textContent = msg;
          successMsg.style.display = 'block';
        }
        if (errorMsg instanceof HTMLElement) {
          errorMsg.style.display = 'none';
        }
      };

      const trackSubscription = (isSuccess) => {
        const w = window;
        if (!w.ugGa4Track || typeof w.ugGa4Track.subscriptionEvent !== 'function') {
          return;
        }
        try {
          w.ugGa4Track.subscriptionEvent({ isSuccess, position: 'nexode-air-template' });
        } catch (_) {}
      };

      submitBtn.addEventListener('click', async () => {
        const emailVal = input.value.trim();

        if (!validateEmailFormat(emailVal)) {
          showError(emailError);
          return;
        }

        if (privacyCheckbox instanceof HTMLInputElement && !privacyCheckbox.checked) {
          showError(privacyError);
          return;
        }

        if (errorMsg instanceof HTMLElement) {
          errorMsg.style.display = 'none';
        }
        if (successMsg instanceof HTMLElement) {
          successMsg.style.display = 'none';
        }

        if (submitBtn.classList.contains('loading')) {
          return;
        }

        submitBtn.classList.add('loading');

        try {
          const response = await fetch(FORM_SUBMIT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: emailVal,
              accepts_marketing:
                privacyCheckbox instanceof HTMLInputElement && privacyCheckbox.checked ? 'on' : 'off',
              form_id: formId
            })
          });

          let data = null;
          try {
            data = await response.json();
          } catch (_) {
            showError(failText);
            trackSubscription(false);
            return;
          }

          const isSuccess = data && data.status === 'success';
          if (isSuccess) {
            input.value = '';
            showSuccess(successText);
          } else {
            const serverMsg =
              (data && data.errors && data.errors.email && data.errors.email[0]) ||
              (data && data.message) ||
              failText;
            showError(serverMsg);
          }
          trackSubscription(!!isSuccess);
        } catch (_) {
          showError(failText);
          trackSubscription(false);
        } finally {
          submitBtn.classList.remove('loading');
        }
      });

      input.addEventListener('keydown', (e) => {
        if (e instanceof KeyboardEvent && e.key === 'Enter') {
          e.preventDefault();
          submitBtn.click();
        }
      });
    }
  }

  customElements.define(TAG_NAME, NexodeAirTemplateSection);
})();
